import { stepUpFresh, ensureStepUp } from '../stepup.js';
import {
  getProjectById, getByPath, getProjectInvoices, listInstructions, addInstruction, updateInstruction, resumeIfHeld,
  documentsForProject, updateDocument, timesheetsForProject, setTimesheetRowTraining, asDate, usersById, updateProjectFields,
  addRecordChange, listRecordChanges,
  listPendingSuggestions, projectEmails, feedbackForProject, listSiteVisits, financeRates,
  notesForProject, addNote, createTask,
  addProjectIssue, projectIssuesForProject, updateProjectIssue, setInvoiceInstruction,
  allProjects, allEnquiries, allInvoices, ACTIVE_STATUSES, cascadeVoidToEnquiry,
  allClients, changeProjectClient, getInstructionDraft, leadRefPath,
} from '../db.js';
import { pickablePeople, personChange, siteFields, dayValue, openPersonPicker, openAddressEditor, openDateEditor, openRefsEditor } from './inline-edit.js';
import { REF_KINDS, refsOf, addRef, refsFields, refChanges } from '../project-refs.js';
import { openClientPicker, openClientChangeConfirm } from './client-picker.js';
import { confirmCopy, raisedInvoices, properName, folderRename, applyClientChange } from '../client-pick.js';
import { openDocSheet } from './doc-sheets.js';
import { mountProjectTasks } from './project-tasks.js';
import { projectTasks, setTaskState, updateTask, taskActivity, addTaskActivity } from '../db.js';
import { dayKey, isOpen, teamFrom, meIn } from '../tasks.js';
import { openNewTask } from './task-new.js';
import { schemeOf, escapeHtml, safeUrl, statusCell, initials, titleCase, money, fmtDate, hoursHM, pill, typeSelect, secretFig, PROJECT_TYPES, schemeLabel, toastV4 } from '../ui.js';
import { drivePanel, openFile } from './google-panels.js';
import { openOverlay } from './overlay.js';
import { attachPlacesAutocomplete } from '../places-ui.js';
import { hasToken, listThreads, listFolder, gmailThreadUrl, findDriveFolder, liveThread, getDriveFile, renameDriveFile, driveWriteToken } from '../google-api.js';
import { openMailReader } from './mail-reader.js';
import { normArchiver } from './mail-correspondence.js';
import { mountCorrespondence } from './wa-timeline.js';
import { canWhatsApp } from '../wa-access.js';
import { getFunctions, httpsCallable } from '../fb/functions.js';
import { app } from '../firebase.js';
import { openSiteVisit } from './site-visit.js';
import { auditDocumentControl, policyQuestion } from '../document-control.js';
import { openSiteReport } from './site-report.js';
import { mark } from '../marks.js';
import { canSee, navItem, canCalculate, canReports } from '../nav.js';
import { calcIcon, goToCalculate } from './calc-icon.js';
import { headerBack } from '../header.js';
import { renderPill } from './enum-pill.js';
import { openEndingConfirm } from './ending-confirm.js';
import { openDialog, openPanel } from './dialog.js';
import { orgSetting } from '../org.js';
import { openInstructionDraft, openAddInstructionQuick, candidateEnquiries, loadCatalogue } from './instruction-draft.js';
import { sourceFromLoaded, openProjectThread } from './golden-thread-open.js';
import { mountSiteConstraints, siteCheckEvidence } from './site-checks-card.js';
import { threadFor } from '../golden-thread.js';
import { callFunction } from '../db.js';
import { resumeToken } from '../google-api.js';
import { loadProjects, evaluate } from './files.js';
import { renderFolderView } from './files-folder.js';
import { BINNED } from '../register-status.js';
import { swrMany, driveKey, driveStore } from '../drive-cache.js';
import { startDriveLive } from '../drive-live.js';
import * as PX from './project-detail-parts.js';
import { navIcon } from '../nav-icons.js';
import { stageText, openStagePicker, openProjectMenu, requestLogTime } from './project-menus.js';
import { goToView } from './calc-icon.js';
import { tierOf, tierText } from './site-checks-ui.js';
import { openRowMenu } from './row-menu.js';
import { openSetFee, openReviseFee, openFeeHistory } from './instruction-fee.js';
import { projectFee } from '../instruction-fee-model.js';

// Stage pill colours (p85: stage pills step navy darker as work moves forward) —
// the classes ui.js pill() picks, so a stage reads the same on every screen.
const PD_STAGE_CLS = {
  'Not Started': 'p-grey-cool', 'In Progress': 'p-blue', Review: 'p-blue2', 'Ready to Issue': 'p-navy',
  'Part Issued': 'p-grey', Issued: 'p-ink', Complete: 'p-ok', 'On Hold': 'p-grey', Void: 'p-grey',
};

// Days a project has sat at its current status (from statusUpdatedAt).
function reviewStaleDays(project) {
  const t = asDate(project.statusUpdatedAt);
  if (!t) return 0;
  return Math.floor((Date.now() - t.getTime()) / 86400000);
}

const MONEY_ROLES = ['Owner', 'Director', 'Finance', 'Coordinator'];
const REVIEWER_ROLES = ['Owner', 'Director', 'Finance', 'Coordinator'];
/**
 * A stored reference, as a path relative to the org.
 *
 * Two shapes are in the data and both are legitimate: the V1 sync writes the
 * ABSOLUTE "organisations/asp/clients/x", and the won-enquiry flow writes the
 * RELATIVE "clients/x". Blindly dropping two segments turned the relative form
 * into an EMPTY string — and orgPath('') is "organisations/asp", an even number
 * of segments, which Firestore happily resolves as a DOCUMENT: the org record
 * itself, whose name is "ASP".
 *
 * So every project created from a won enquiry loaded ASP as its client. The
 * name on screen looked right only because a workaround below substitutes the
 * project's own clientName when the client "is" ASP — which hid this for weeks
 * while the Client link and the whole golden thread quietly showed another
 * company's enquiries, projects and invoices (Karan 04/09).
 */
const relPath = (ref) => {
  const s = String(ref || '').trim();
  if (!s) return '';
  return s.startsWith('organisations/') ? s.split('/').slice(2).join('/') : s;
};

const parseHM = (v) => {
  if (!v) return 0;
  const s = String(v).trim();
  if (s.includes(':')) { const [h, m] = s.split(':'); return (parseInt(h || 0, 10) * 60) + parseInt(m || 0, 10); }
  const f = parseFloat(s); return isNaN(f) ? 0 : Math.round(f * 60);
};

export async function renderProjectDetail(mount, projectId, session, onBack, { backLabel = 'Projects', onOpenLead, onOpenEnquiry, onOpenClient, onAskTrack, quiet = false } = {}) {
  // No back button on the page: the header line carries "← Projects" (§4).
  // Asserted on EVERY render, which is what brings it back after the time
  // breakdown — one level deeper — borrowed it for "← Back to 8570".
  headerBack(backLabel, onBack);

  /**
   * PAINT WHAT WE ALREADY KNOW, IMMEDIATELY.
   *
   * The record used to show the word "Loading…" for two seconds and then the
   * whole page at once. But the list you just clicked is in cache — the number,
   * the client, the scheme and the address are all in memory before this
   * function is called, and they are the four things you looked at the row to
   * check. Only the panels underneath need the server.
   *
   * So the masthead goes up now and the rest arrives into it. The full render
   * below replaces this wholesale; nothing here is load-bearing, and if the
   * cached row is missing (a deep link into a cold tab) it falls back to the
   * word it used to show.
   */
  const known = (await allProjects().catch(() => [])).find((x) => x.id === projectId) || null;
  // Drawn with the record's own header classes, so the number lands at its
  // final size and place and the full render replaces it without a jump.
  mount.innerHTML = known
    ? `<div class="pdx-page pd4">
         <nav class="crumbs pd4-crumbs" aria-label="Breadcrumb"><span>${escapeHtml(backLabel || 'Projects')}</span><span aria-hidden="true">/</span><span>${escapeHtml(String(known.number || '—'))}</span></nav>
         <header class="pd4-head">
           <div class="pd4-head-l">
             <h1 class="pd4-title"><span class="pd4-no">${escapeHtml(String(known.number || '—'))}</span><span class="pd4-client">${escapeHtml(known.clientName || known.client || '')}</span></h1>
             <div class="pd4-meta">${escapeHtml(known.title || known.description || '')}<span class="pd-first-wait"> · Reading the record…</span></div>
           </div>
         </header>
       </div>`
    : '<p class="muted" style="margin-top:24px">Loading…</p>';

  const isMoney = session.roles.some((r) => MONEY_ROLES.includes(r));
  const isReviewer = session.roles.some((r) => REVIEWER_ROLES.includes(r));
  // Edit-patterns (6d panel / 6e status pill / 6g endings / 6h change list) ride
  // the same Files/Mail pilot gate — strictly ADDITIVE and OFF for everyone outside
  // the pilot, so the record (golden thread, tabs, figures) renders unchanged.
  const editPatterns = canSee(navItem('files'), session.roles, session.email);
  // Changing status alters others' screens → Owner/Director only (6e). Non-owner/
  // director pilot viewers get the inert pill with the holding role named.
  const canStatus = session.roles.some((r) => ['Owner', 'Director'].includes(r));
  const navOpts = { backLabel, onOpenLead, onOpenEnquiry, onOpenClient, onAskTrack }; // preserved across re-renders
  let project, instructions = [], client = null, lead = null, invoices = [], documents = [], suggestions = [], visits = [], users = {}, notes = [], sheets = [];
  const TIME_PENDING = Symbol('time-pending');
  let timePending = false, sheetsP = null;
  try {
    project = await getProjectById(projectId);
    if (!project) throw new Error('Project not found');
    // Started here so its round trip runs alongside the cached reads below, not
    // after them — the deadline is measured from now.
    sheetsP = timesheetsForProject(projectId, project.number).catch(() => []);
    // ONE WAVE (Karan 17/09). The invoices and the timesheets used to be
    // awaited after this block had already resolved — three round trips deep
    // instead of one — so the slowest read did not even start until everything
    // else had finished. Every one of these carries its own .catch, so a single
    // failing read costs its own panel and not the page.
    [instructions, client, lead, documents, suggestions, visits, users, notes, invoices, sheets] = await Promise.all([
      listInstructions(projectId).catch(() => []),
      project.clientRef ? getByPath(relPath(project.clientRef)).catch(() => null) : null,
      project.lead ? getByPath(relPath(project.lead)).catch(() => null) : null,
      project.number ? documentsForProject(project.number).catch(() => []) : [],
      (isReviewer && project.number) ? listPendingSuggestions(project.number).catch(() => []) : [],
      listSiteVisits(projectId).catch(() => []),
      usersById().catch(() => ({})),
      project.number ? notesForProject(String(project.number)).catch(() => []) : [],
      isMoney ? getProjectInvoices(projectId).catch(() => []) : [],
      // Only the weeks that booked time to THIS project (Karan 17/09) — it used
      // to be all 600 of them, 1.2 MB, to use one row of one week.
      //
      // AND IT DOES NOT GET TO HOLD THE RECORD (perf stage 3, 24/09). Every other
      // read here is answered from the on-disk cache; this one must reach the
      // server, because membership of the query depends on a field written
      // server-side and a stale cache reports a WRONG number of hours (see
      // db.js). So it keeps its round trip and loses its veto: past the deadline
      // the record renders with the time figure marked as still arriving, never
      // as zero, and re-renders when the real answer lands.
      Promise.race([sheetsP, new Promise((r) => { setTimeout(() => r(TIME_PENDING), 1200); })]),
    ]);
    timePending = sheets === TIME_PENDING;
    if (timePending) sheets = [];
  } catch (e) {
    mount.innerHTML = `<p class="error" style="margin-top:24px">Couldn't load project: ${escapeHtml(e.message || e)}</p>`;
    return;
  }

  const instr1 = instructions.find((i) => String(i.seq) === '1') || instructions[0] || null;
  // Per-instruction lead (Karan 28/07): an instruction can be led by someone other
  // than the project lead; #1 falls back to the project lead.
  const tailRef = (r) => String(r || '').split('/').pop() || null;
  const instrLeadName = (i) => {
    const lid = tailRef(i.lead) || (String(i.seq) === '1' ? tailRef(project.lead) : null);
    return (lid && users[lid]) ? titleCase(users[lid].displayName || lid) : null;
  };
  const userOptions = Object.entries(users).map(([id, u]) => ({ id, name: titleCase(u.displayName || id) }))
    .sort((a, b) => a.name.localeCompare(b.name));
  // Which instruction a document is tagged to (docs live in one folder; the
  // instruction is a Track tag). Defaults to #1 (Karan 28/07).
  const docInstr = (d) => Number(d.instructionSeq) || 1;
  const multiInstr = instructions.length > 1;
  // V1 sync sometimes links our own org record as the client (board 08/08
  // 22:00) — when the project itself names a different client, believe it.
  let clientName = (client && client.name) || project.clientName || '—';
  if (client && /^asp\b/i.test(String(client.name || '').trim()) && project.clientName
      && String(project.clientName).trim().toLowerCase() !== String(client.name).trim().toLowerCase()) {
    clientName = project.clientName;
  }
  const canChangeClient = isReviewer;   // v4.6.2: the roles that edit project details
  const currentClientId = tailRef(project.clientRef) || '';
  const shownClient = clientName === '—' ? '' : clientName;
  // The client's scheme first; the project's copy only if it is a real scheme.
  const scheme = schemeOf(client && client.type, project.scheme);
  const leadId = project.lead ? project.lead.split('/').pop() : null;
  const leadName = titleCase((lead && lead.displayName) || (leadId && users[leadId] && users[leadId].displayName) || leadId || '');
  const addr = (project.siteAddress && project.siteAddress.line1) || '';
  // The V1 sync often mirrors the description into service — don't say it twice
  // in the header (Karan 08/08 22:00 board issue was partly this).
  const norm = (v) => String(v || '').replace(/\s+/g, ' ').trim().toLowerCase();

  // Fee — the recorded project fee wins (Record fee flow, Karan 04/08), then any
  // instruction fees, then the linked enquiry's quoted fee as a fallback. We also
  // keep the enquiry's PROPOSED fee for the Record-fee dialog prefill.
  let enqData = null, proposedFee = null;
  if (instr1 && instr1.enquiryRef) {
    enqData = await getByPath(relPath(instr1.enquiryRef)).catch(() => null);
    if (enqData) proposedFee = Number(enqData.quotedFee) || Number(enqData.feeDraft && enqData.feeDraft.finalFeeExVat) || null;
  }
  // v4.20.0: when every instruction carries its fee, their sum IS the project's
  // fee (8451 revised reads the revised fee, not V1's figure).
  let fee = projectFee(project, instructions) || (proposedFee || null);
  const feeRecorded = Number(project.fee) > 0 || instructions.some((i) => Number(i.fee) > 0);
  const budgetHrs = instructions.reduce((s, i) => s + (Number(i.budgetHours) || 0), 0) || null;
  const due = (instr1 && instr1.due) || project.due || null;
  // A voided invoice is no longer owed or invoiced (finance.js rule), so it is not counted.
  const invoicedTotal = invoices.reduce((s, iv) => s + (iv.v2Voided ? 0 : (Number(iv.amount) || 0)), 0);
  // Golden thread (Karan 23/07): which instruction each invoice sits on. The V1
  // sync sets instructionRef (always seq-1); a V2-only v2InstructionSeq override
  // lets Finance re-assign without touching the master sheet. Falls back to #1.
  const invInstrSeq = (iv) => {
    const v = Number(iv.v2InstructionSeq);
    if (v) return v;
    const m = String(iv.instructionRef || '').match(/instructions\/(\d+)/);
    return m ? Number(m[1]) : 1;
  };
  const invByInstr = {}; // seq → invoiced total
  invoices.forEach((iv) => { if (iv.v2Voided) return; const s = invInstrSeq(iv); invByInstr[s] = (invByInstr[s] || 0) + (Number(iv.amount) || 0); });

  // Time tracked: minutes logged against this project across everyone's
  // timesheets (visible to reviewers; others see their own sheets only).
  let trackedMins = 0;
  const minsByUser = {}; // email-local → minutes (for the cost calc)
  const minsByInstr = {}; // instruction seq → minutes (golden thread rollup, Karan 23/07)
  const timeEntries = []; // per-row detail for the breakdown drill-down (Karan 14/07)
  try {
    for (const s of sheets) {
      if (s.imported) continue; // historical weeks are already in the histMins rollup — don't double-count
      (s.rows || []).forEach((r, rowIndex) => {
        if (r.projectId === projectId || (project.number && r.number === project.number)) {
          const mins = Object.values(r.days || {}).reduce((x, v) => x + parseHM(v), 0);
          if (!mins) return;
          trackedMins += mins;
          const iseq = Number(r.instructionSeq) || 1;
          minsByInstr[iseq] = (minsByInstr[iseq] || 0) + mins;
          const who = String(s.userEmail || '').split('@')[0];
          if (who) minsByUser[who] = (minsByUser[who] || 0) + mins;
          timeEntries.push({
            who: s.userName || who, whoKey: who, mins, weekStart: s.weekStart || null,
            task: r.task || r.description || r.instructionRef || '', note: r.note || '',
            training: !!r.training,
            sheetId: s.id, rowIndex, // for the "log as training" toggle
          });
        }
      });
    }
  } catch (e) { /* non-reviewers may only read their own — partial is fine */ }

  // Historical time imported from V1 "Day Wise Timesheet" (Karan 12/07): a
  // per-project rollup so pre-launch hours count without recreating thousands
  // of weekly docs. Pre-launch history + live V2 sheets never overlap.
  if (Number(project.histMins)) {
    trackedMins += Number(project.histMins);
    const hb = project.histByUser || {};
    for (const who in hb) minsByUser[who] = (minsByUser[who] || 0) + (Number(hb[who]) || 0);
  }

  // Cost to date (charge-out basis) = Σ (each person's tracked time × their
  // charge-out rate). Plus a Finance-only TRUE cost = Σ (time × break-even rate),
  // from the Finance-only financeRates collection (Karan 14/07).
  const isFinance = session.roles.some((r) => ['Owner', 'Director', 'Finance'].includes(r));
  // Cost rates need a fresh biometric check (security review 17/10/2026): read
  // only when one is live; otherwise the figure offers the unlock, never a £0.
  const costUnlocked = isFinance && await stepUpFresh();
  let projectCost = null, trueCost = null, usersMap = {}, costRates = {};
  if (isMoney && trackedMins && !timePending) {
    try {
      const [users, rates] = await Promise.all([
        usersById(),
        costUnlocked ? financeRates().catch(() => ({})) : Promise.resolve({}),
      ]);
      usersMap = users || {}; costRates = rates || {};
      projectCost = Object.entries(minsByUser).reduce((sum, [who, mins]) => {
        const rate = (usersMap[who] && Number(usersMap[who].chargeOutRate)) || 60;
        return sum + (mins / 60) * rate;
      }, 0);
      if (costUnlocked) {
        trueCost = Object.entries(minsByUser).reduce((sum, [who, mins]) =>
          sum + (mins / 60) * (Number(costRates[who]) || 0), 0);
      }
    } catch (e) { projectCost = (trackedMins / 60) * 60; }
  }

  const summary = [
    `Project ${project.number || projectId}${clientName !== '—' ? ` for ${clientName}` : ''}`,
    project.service ? ` — ${project.service}` : '',
    addr ? ` at ${addr}` : '',
    `. Current status: ${project.status || 'unknown'}.`,
    isMoney && fee ? ` Fee ${money(fee)}, ${money(invoicedTotal)} invoiced across ${invoices.length} invoice${invoices.length === 1 ? '' : 's'} to date.` : '',
  ].join('');

  const numbers = [];
  numbers.push(['Budget (hrs)', budgetHrs != null ? String(budgetHrs) : '—']);
  // Time tracked here is plain text — the interactive figure (opens the breakdown)
  // lives in the KPI strip via #open-time; a second one would duplicate the id.
  numbers.push(['Time tracked', timePending ? 'still arriving' : (trackedMins ? hoursHM(trackedMins / 60) : '—')]);
  // Costs show the break-even (true) cost only, plus margin vs the fee — Finance
  // only (Karan 14/07). The charge-out "cost" figure is no longer shown.
  if (isFinance) numbers.push(['True cost', costUnlocked ? secretFig(trueCost ? money(trueCost) : '—', isFinance) : '<button class="fx-link" data-cost-unlock title="Cost rates need Face ID, Touch ID or your device PIN">Unlock</button>']);
  if (isFinance) {
    const rev = fee || invoicedTotal || 0;
    const marginPct = (trueCost && rev) ? Math.round(((rev - trueCost) / rev) * 100) : null;
    numbers.push(['Margin', secretFig((marginPct != null)
      ? `${marginPct}% <span class="g-note" style="font-size:11px">(${money(rev - trueCost)})</span>`
      : '—', isFinance)]);
  }
  const dueVal = project.due || due;
  // Plain here — the editable Due date (#edit-due) is in the KPI strip.
  numbers.push(['Due date', dueVal ? fmtDate(dueVal) : '—']);
  if (isMoney) {
    numbers.push(['Fee', fee ? money(fee) : '—']);
    numbers.push(['Invoiced', invoicedTotal ? money(invoicedTotal) : '—']);
  }

  const issued = documents.filter((d) => d.direction === 'issued');
  const received = documents.filter((d) => d.direction === 'received');
  const dcAudit = auditDocumentControl(project, documents);
  const dcTone = dcAudit.status === 'blocked' ? 'error' : dcAudit.status === 'warning' ? 'warn' : 'ok';
  const dcLabel = dcAudit.status === 'blocked' ? 'Blocked' : dcAudit.status === 'warning' ? 'Warnings' : 'Pass';

  // Supersession (Karan 13/07): a drawing was appearing twice — two revisions
  // of the same doc number, both showing "current". Within each doc number the
  // most recently issued revision is current; earlier revisions are superseded.
  // Display-only (the register data is untouched; Drive moves stay with the
  // track@ agent). Date is the primary signal — you issue a newer rev later —
  // with the revision string (numeric-aware) as the tie-break.
  const supersededIds = (() => {
    const groups = new Map();
    issued.forEach((d) => {
      const key = String(d.docNumber || '').trim().toLowerCase();
      if (!key) return; // no number → can't supersede; leave as issued
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(d);
    });
    const out = new Set();
    const timeOf = (d) => { const t = new Date(d.date || d.createdAt || 0).getTime(); return Number.isNaN(t) ? 0 : t; };
    groups.forEach((docs) => {
      if (docs.length < 2) return;
      const newestFirst = [...docs].sort((a, b) =>
        timeOf(b) - timeOf(a) ||
        String(b.revision || '').localeCompare(String(a.revision || ''), undefined, { numeric: true }));
      newestFirst.slice(1).forEach((d) => out.add(d.id)); // everything but the newest
    });
    return out;
  })();
  // A binned row (its file moved to Drive's bin from Files, v4.23.10) says so — never "current".
  const effStatus = (r) => (String(r.status || '').toLowerCase() === BINNED ? BINNED
    : (supersededIds.has(r.id) || r.status === 'superseded') ? 'superseded' : (r.status || 'current'));

  // ── Drawing review lands where documents already open (Karan 08/08 — no
  // separate Drawings & models screen): a register row or Drive-panel file
  // opened from the Documents tab routes through openFile with a `reviewer`
  // hint, so PDFs open in the full-screen reviewer instead of the plain PDF
  // viewer. Pilot-gated (editPatterns) like every new module. The register's
  // Drive pointer is inconsistent between writers (archiver: driveFileId,
  // site-report: driveId, older rows: only driveUrl) — take any.
  const drDriveId = (d) => d.driveFileId || d.driveId
    || (String(d.driveUrl || '').match(/\/d\/([A-Za-z0-9_-]{20,})/) || [])[1]
    || (String(d.driveUrl || '').match(/[?&]id=([A-Za-z0-9_-]{20,})/) || [])[1] || null;

  // Register row — the 5d treatment: date · number/rev over filename · #instr ·
  // status, with the reviewer controls tucked under the status label so the
  // common (read-only) row still reads as the artboard.
  const regRow = (r) => { const st = effStatus(r); const sup = st === 'superseded' || st === BINNED;
    return `<div class="doc-row" data-instr="${docInstr(r)}" style="display:grid;grid-template-columns:74px 1fr 44px auto;gap:14px;align-items:center;padding:11px 0;border-bottom:1px solid var(--hair);${sup ? 'opacity:0.7;' : ''}">
    <span class="g-mono" style="font-size:11.5px;color:var(--ink-3)">${fmtDate(r.date || r.createdAt)}</span>
    <span style="min-width:0;display:flex;flex-direction:column;gap:2px">
      <span class="g-mono" style="font-size:12.5px;font-weight:500;${sup ? 'color:var(--ink-3);text-decoration:line-through;' : ''}">${escapeHtml((r.docNumber || '—') + (r.revision ? ' ' + r.revision : ''))}</span>
      <span style="font-size:11.5px;color:var(--ink-3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${r.driveUrl || (editPatterns && drDriveId(r)) ? `<a href="${escapeHtml(safeUrl(r.driveUrl || '#'))}" ${editPatterns && drDriveId(r) ? `class="dr-doc" data-doc="${escapeHtml(r.id)}"` : ''} target="_blank" rel="noopener" style="color:inherit">${escapeHtml(r.filename || '')}</a>` : escapeHtml(r.filename || '')}</span>
    </span>
    <span class="g-mono" style="font-size:11px;color:var(--ink-3)">#${docInstr(r)}</span>
    <span style="display:flex;flex-direction:column;align-items:flex-end;gap:4px">
      <span style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;text-align:right;color:${sup ? 'var(--ink-3)' : 'var(--brand-blue)'}">${st === BINNED ? 'binned' : sup ? 'superseded' : 'current'}</span>
      ${(r.needsReview || isReviewer) ? `<span style="display:inline-flex;gap:8px;align-items:center">
        ${r.needsReview ? `<button class="linkbtn doc-review" data-id="${escapeHtml(r.id)}" title="Needs review — click to resolve" style="color:var(--brand-orange);font-size:11px">⚑</button>` : ''}
        ${(isReviewer && multiInstr) ? `<button class="linkbtn doc-instr" data-id="${escapeHtml(r.id)}" data-seq="${docInstr(r)}" title="Tag to an instruction" style="font-size:11px">✎</button>` : ''}
        ${isReviewer ? `<button class="linkbtn doc-flip" data-id="${escapeHtml(r.id)}" data-dir="${r.direction === 'issued' ? 'received' : 'issued'}" title="Wrong list? Move this document" style="font-size:11px">→ ${r.direction === 'issued' ? 'Received' : 'Issued'}</button>` : ''}
      </span>` : ''}
    </span>
  </div>`; };

  // Forward-only lifecycle. Clicking the status pill advances one step (Karan 09/07);
  // the ✎ opens a picker to jump to a specific status. Both lead-roles only.
  const WORK_ORDER = ['Not Started', 'In Progress', 'Review', 'Ready to Issue', 'Part Issued', 'Issued', 'Complete'];
  const curIdx = Math.max(0, WORK_ORDER.indexOf(project.status || 'Not Started'));
  const nextStatus = curIdx < WORK_ORDER.length - 1 ? WORK_ORDER[curIdx + 1] : null;

  // ── Project Status card — the golden thread, computed from real state (v2 handoff, Karan 04/08) ──
  const daysSince = (d) => { if (!d) return null; const t = new Date(d).getTime(); return isNaN(t) ? null : Math.floor((Date.now() - t) / 86400000); };
  // A deadline arrives as a Timestamp, an ISO string or the V1 sheet's
  // "26/05/2026" — new Date() reads the last as Invalid, which put "NaN days to
  // the deadline" on 7890 and called a passed deadline "on track" (07/10/2026).
  const ukDate = (v) => { const m = String(v || '').match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); return m ? new Date(+m[3], +m[2] - 1, +m[1], 12) : null; };
  const dueD = dueVal ? (asDate(dueVal) || ukDate(dueVal)) : null;
  const dueMs = dueD && !isNaN(dueD.getTime()) ? dueD.getTime() : null;
  const overdueDays = dueMs != null ? Math.max(0, Math.floor((Date.now() - dueMs) / 86400000)) : 0;
  const nInstr = instructions.length;
  const canFinance = isFinance;
  const enqNo = (enqData && enqData.number) || (instr1 && instr1.enquiryRef ? relPath(instr1.enquiryRef).split('/').pop() : null);

  // The latest week anyone booked time here — "last entry 22 Jul" under WORKED ON.
  const lastEntryAt = timeEntries.map((t) => t.weekStart).filter(Boolean).sort().pop() || null;
  const PX_day = (v) => { const d = asDate(v) || new Date(v); return !d || isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); };
  // "14 Jul 2026" — the stage chain and linked records read as dates, not as a form field.
  const longDay = (v) => { const d = asDate(v) || new Date(v); return d && !isNaN(d) ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''; };
  // Where the deadline comes from — said plainly; Track holds no turnaround rule.
  const deadlineBasis = !dueVal ? '' : (instr1 && instr1.due) ? 'Deadline set on instruction 01' : 'Deadline set on the project';

  // 7 nodes: ENQUIRY · FEE · INSTRUCTED · WORKED ON · DEADLINE · ISSUED · INVOICED.
  const ENQUIRIES_FROM = Date.parse('2026-07-13T00:00:00Z');   // Track V2 live — enquiries kept from here
  const openedD = asDate(project.createdAt) || asDate(instr1 && (instr1.instructedAt || instr1.createdAt));
  const preTrack = !enqData && !enqNo && !!openedD && openedD.getTime() < ENQUIRIES_FROM;
  const rawNodes = [
    preTrack
      // Opened before Track kept enquiries (V2 went live 13/07/2026): no
      // enquiry is the expected state, not a break (Karan 07/10/2026).
      ? { label: 'ENQUIRY', value: 'before Track', record: 'opened before enquiries were kept', state: 'done', link: false, id: null }
      : { label: 'ENQUIRY', value: (instr1 && instr1.enquiredAt) ? longDay(instr1.enquiredAt) : (enqData && (enqData.receivedAt || enqData.createdAt)) ? longDay(enqData.receivedAt || enqData.createdAt) : (enqData ? 'linked' : 'none'), record: enqNo || 'no enquiry', state: enqData || enqNo ? 'done' : 'todo', link: !!enqNo, id: enqNo && onOpenEnquiry ? 'open-enquiry-3' : null },
    { label: 'FEE', value: feeRecorded ? (isMoney ? money(fee) : '••••') : (proposedFee ? 'not recorded' : 'none'), record: feeRecorded ? 'recorded on project' : (proposedFee ? 'proposal in email' : 'no proposal'), state: feeRecorded ? 'done' : (isMoney ? 'broken' : 'todo'), link: true },
    { label: 'INSTRUCTED', value: nInstr ? longDay((instr1 && (instr1.instructedAt || instr1.createdAt)) || project.createdAt) : 'none', record: nInstr ? `${nInstr} instruction${nInstr === 1 ? '' : 's'}` : 'not instructed', state: nInstr ? 'done' : 'todo', link: false },
    { label: 'WORKED ON',
      value: timePending ? '…' : (trackedMins ? hoursHM(trackedMins / 60) : 'not started'),
      record: timePending ? 'still arriving' : (trackedMins ? (lastEntryAt ? `last entry ${PX_day(lastEntryAt)}` : 'time logged') : 'no time logged'),
      state: timePending ? 'todo' : (trackedMins ? 'done' : 'todo'), pending: timePending, link: false },
    { label: 'DEADLINE', value: dueVal ? longDay(dueVal) : 'not set', record: dueVal ? (overdueDays > 0 ? `${overdueDays} days overdue` : 'on track') : 'no deadline', state: !dueVal ? 'todo' : (overdueDays > 0 && !['Issued', 'Complete'].includes(project.status)) ? 'late' : 'done', link: false },
    { label: 'ISSUED', value: issued.length ? `${issued.length} issued` : 'nothing yet', record: issued.length ? 'register filled' : 'register empty', state: issued.length ? 'done' : 'todo', link: true },
    { label: 'INVOICED', value: canFinance ? money(invoicedTotal || 0) : '••••', record: canFinance ? (invoicedTotal ? 'invoice raised' : 'no invoice raised') : 'finance access', state: invoicedTotal ? 'done' : 'todo', link: canFinance },
  ];
  // A stage still waiting on its read is UNKNOWN, not broken. Reporting "the
  // thread breaks at worked on" while the hours are still arriving states a
  // fault that may not exist, so a pending node is skipped: the next genuine
  // gap is named instead, and the count holds until the answer is in.
  const firstBreak = rawNodes.findIndex((n) => n.state !== 'done' && !n.pending);
  const threadIntact = rawNodes.filter((n) => n.state === 'done').length;
  const breakNode = firstBreak >= 0 ? rawNodes[firstBreak] : null;
  const threadNarrative = !breakNode ? 'Every stage of this project is accounted for.'
    : breakNode.label === 'FEE' ? 'The fee was proposed at enquiry but never recorded against the project, so cost, margin and invoicing stay blocked.' + (overdueDays > 0 ? ` The deadline passed ${overdueDays} days ago.` : '')
    : breakNode.label === 'DEADLINE' ? `The deadline passed ${overdueDays} days ago and the project is still ${escapeHtml(project.status || 'open')}.`
    : `The thread breaks at ${breakNode.label.toLowerCase()} — ${breakNode.record}.`;
  const threadAction = breakNode && breakNode.label === 'FEE' && isMoney ? { label: 'Record fee', id: 'thread-fee' } : (isReviewer && !dueVal ? { label: 'Set deadline', id: 'thread-due' } : null);

  // ── Benchmark composition (§1.2/§1.4/§1.5) ────────────────────────────────
  // Map the computed 7-node states onto the four bmChain states:
  //   done → 'passed' (navy) · broken|late → 'attention' (orange) · the frontier
  //   todo node → 'current' (blue+halo) · every other todo → 'unreached' (grey).
  // The thread BREAKS at the first node needing attention: break:true puts a gap
  // on the connector into it and dashes every connector after (§1.5, §3).
  const firstAttention = rawNodes.findIndex((n) => n.state === 'broken' || n.state === 'late');
  const NODE_STATE = { done: 'passed', broken: 'attention', late: 'attention' };
  const chainNodes = rawNodes.map((n, idx) => ({
    label: n.label, value: n.value, record: n.record,
    state: NODE_STATE[n.state] || (idx === firstBreak ? 'current' : 'unreached'),
    break: firstAttention >= 0 && idx === firstAttention,
  }));

  // The header anchor is the project NUMBER (5b) — an identifier, not a derived
  // figure — so the four status-card figures (built at render time) carry the
  // work/money split, with the deadline free to appear as one of them.
  const timeFigVal = timePending ? '…' : (trackedMins ? hoursHM(trackedMins / 60) : '0:00');

  // The deadline repair action reuses the real #edit-due dialog handler.
  const actId = threadAction ? (threadAction.id === 'thread-due' ? 'edit-due' : threadAction.id) : null;

  const docCount = issued.length + received.length;

  // ── The golden thread (Track Golden Thread.html, 04/09/2026) ──────────────
  // One continuous history — enquiry and fee, instruction and site decision,
  // invoice and payment, meeting note and call — built from what this page has
  // already loaded, so the mark on the header, the figure on the status card
  // and the panel itself are the same arithmetic. The panel it replaced listed
  // linked records in four flat groups: a directory, which is a thing you
  // consult rather than a thing that tells you something.
  const gtSource = sourceFromLoaded({
    project: { ...project, id: projectId }, instructions, invoices, notes, visits, enquiry: enqData, isMoney,
  });
  const gt = threadFor(gtSource, {
    hasFee: feeRecorded, fee: isMoney && fee ? money(fee) : null,
    meId: session.email, meName: session.displayName,
  });

  // ── 5b display derivations ────────────────────────────────────────────────
  const ddmm = (v) => { const d = fmtDate(v); return (d && d.length >= 5) ? d.slice(0, 5) : d; };
  const daysToDeadline = dueMs != null ? Math.ceil((dueMs - Date.now()) / 86400000) : null;
  const deadlinePhrase = !dueVal ? 'No deadline set'
    : overdueDays > 0 ? `${overdueDays} day${overdueDays === 1 ? '' : 's'} overdue`
    : `${Math.max(0, daysToDeadline)} day${daysToDeadline === 1 ? '' : 's'} to the deadline`;

  // The four figures (5b §figures card) — work and money on SEPARATE figures,
  // role-gated. Deadline is a figure here because the header anchor is the
  // project number (an identifier, not a derived value), not the deadline.
  const lateLive = overdueDays > 0 && !['Issued', 'Complete'].includes(project.status);
  const figs = [{ label: 'Time tracked', value: timeFigVal, sub: timePending ? 'still arriving' : `across ${nInstr || 1} instruction${(nInstr || 1) === 1 ? '' : 's'}`, time: true },
    { label: 'Deadline', value: dueVal ? PX_day(dueVal) : '—', sub: dueVal ? (lateLive ? `${overdueDays} day${overdueDays === 1 ? '' : 's'} overdue` : 'on track') : 'not set', tone: lateLive ? 'var(--brand-orange)' : null, subId: dueVal ? null : (isReviewer ? 'kpi-due' : null) }];
  // The fee figure carries its own repair: "+ Record fee" (design 07/10).
  if (isMoney) figs.push({ label: 'Fee', value: feeRecorded && fee ? money(fee) : '—', sub: feeRecorded ? 'recorded' : '+ Record fee', subId: feeRecorded ? null : 'kpi-fee', subTone: feeRecorded ? null : 'var(--link)' });
  else figs.push({ label: 'Budget', value: budgetHrs ? `${budgetHrs}h` : '—', sub: 'planned hours' });
  if (isFinance) figs.push({ label: 'True cost', value: trueCost ? money(trueCost) : '—', sub: 'floor at break-even', secret: true });
  else if (isMoney) figs.push({ label: 'Invoiced', value: invoicedTotal ? money(invoicedTotal) : '—', sub: invoicedTotal ? 'to date' : 'nothing invoiced yet' });
  else figs.push({ label: 'Issued', value: issued.length ? String(issued.length) : '—', sub: issued.length ? 'in the register' : 'nothing issued' });
  // The golden thread is a PERMANENT node on this card (2b, entry 03), beside
  // fee and true cost — not a badge that appears when something is wrong. A
  // number that only shows up in trouble teaches people to read its absence as
  // "fine", which is exactly the assumption the thread exists to test.
  // The golden thread moved to the masthead (the round mark beside the status,
  // with an orange dot while it has breaks) — the 07/10 design's four figures.

  const figsHtml = figs.map((f, i) => `
    <div class="pdx-fig"${(f.time && trackedMins && !timePending) ? ' id="open-time" role="button" tabindex="0" style="cursor:pointer;' : f.id ? ` id="${f.id}" role="button" tabindex="0" style="cursor:pointer;` : ' style="'}animation:bm-rise 300ms var(--ease-out) ${200 + 40 * i}ms both">
      <span class="pdx-eyebrow">${escapeHtml(f.label)}</span>
      <span class="pdx-fig-val" style="color:${f.tone || 'var(--anchor)'};">${f.secret ? secretFig(escapeHtml(String(f.value)), isFinance) : escapeHtml(String(f.value))}${f.of ? `<span class="pdx-fig-of">${escapeHtml(f.of)}</span>` : ''}</span>
      ${f.subId ? `<button type="button" id="${f.subId}" class="pdx-fig-sub pdx-linkbtn" style="color:${f.subTone || 'var(--link)'};">${escapeHtml(f.sub)}</button>` : `<span class="pdx-fig-sub" style="color:${f.tone || 'var(--ink-3)'};">${escapeHtml(f.sub)}</span>`}
    </div>`).join('');

  // Chain node visuals (5b treatment): the connector runs solid navy up to the
  // frontier (first non-passed node), then dashes; each state has its own dot +
  // three-lines-per-node (label / value / record) colour.
  const NODE_VIS = {
    // --link, not the fixed brand blue: the record line has to read on the Grey dark card too.
    passed:    { bg: 'var(--anchor)', bd: 'var(--anchor)', halo: '', lc: 'var(--ink)', vc: 'var(--ink)', rc: 'var(--link)' },
    current:   { bg: 'var(--link)', bd: 'var(--link)', halo: 'box-shadow:0 0 0 5px var(--halo);', lc: 'var(--link)', vc: 'var(--link)', rc: 'var(--link)' },
    attention: { bg: 'var(--brand-orange)', bd: 'var(--brand-orange)', halo: 'box-shadow:0 0 0 5px rgba(255,80,1,0.20);', lc: 'var(--brand-orange)', vc: 'var(--brand-orange)', rc: 'var(--brand-orange)' },
    unreached: { bg: 'transparent', bd: 'var(--hair-strong)', halo: '', lc: 'var(--hair-strong)', vc: 'var(--hair-strong)', rc: 'var(--hair-strong)' },
  };
  const frontierIdx = chainNodes.findIndex((n) => n.state !== 'passed');
  const chainCut = frontierIdx < 0 ? chainNodes.length : frontierIdx;
  const chainHtml = chainNodes.map((n, i) => {
    const v = NODE_VIS[n.state] || NODE_VIS.unreached; const raw = rawNodes[i];
    const last = i === chainNodes.length - 1;
    const conn = last ? ''
      : (i < chainCut) ? `<span style="flex:1;height:2px;background:var(--anchor);animation:bm-draw 320ms var(--ease-out) ${320 + 80 * i}ms both;transform-origin:left center"></span>`
      : `<span style="flex:1;height:0;border-top:2px dashed var(--hair)"></span>`;
    return `<div style="display:flex;flex-direction:column;gap:9px;min-width:0">
      <div style="display:flex;align-items:center;gap:4px">
        <span style="width:12px;height:12px;border-radius:50%;flex:0 0 12px;background:${v.bg};border:2px solid ${v.bd};${v.halo}animation:bm-pop 260ms var(--ease-out) ${260 + 80 * i}ms both"></span>${conn}
      </div>
      <span style="font-size:9px;font-weight:500;letter-spacing:0.12em;text-transform:uppercase;color:${v.lc}">${escapeHtml(raw.label)}</span>
      <span style="font-size:15px;font-weight:500;letter-spacing:-0.01em;color:${v.vc}">${escapeHtml(String(raw.value))}</span>
      ${raw.id ? `<button type="button" id="${raw.id}" class="pdx-linkbtn" style="font-size:11.5px;color:var(--link);">${escapeHtml(raw.record)}</button>` : `<span style="font-size:11.5px;color:${v.rc}">${escapeHtml(raw.record)}</span>`}
    </div>`;
  }).join('');

  // ── LINKED RECORDS (07/10 design) ────────────────────────────────────────
  // Client (and how many live projects they have), contact, the enquiry it was
  // won from, the fee and the invoices — each opening its record. The site keeps
  // its map link here; the masthead only names it.
  const allP = await allProjects().catch(() => []);   // cached — the list was just read
  const clientKey = (p) => String(p.clientRef || '').split('/').pop() || String(p.clientName || '').trim().toLowerCase();
  const myClient = clientKey(project);
  const liveForClient = myClient ? allP.filter((p) => clientKey(p) === myClient && ACTIVE_STATUSES.includes(p.status)).length : 0;
  const contact = (client && client.contactName) ? [client.contactName, client.contactRole].filter(Boolean).join(' · ') : (project.contactName || '');
  // ══ v4.0.0 — THE PROJECT RECORD (design p07; docs/ui-refresh/01-home-workspace.md §3) ══
  // A breadcrumb, one title line with the stage pill, one meta line, the
  // actions on the right; a ruled strip of four figures; ONE alert with its fix;
  // underline tabs; then Overview in two columns — Instructions, Golden thread
  // and Activity on the left, Next up and Details on the right.
  const lateWord = (n) => `${n} day${n === 1 ? '' : 's'}`;
  const stageNow = project.status || 'Not Started';
  const canMoveStage = isReviewer && !['Void'].includes(stageNow);
  const stagePill = canMoveStage
    ? `<button type="button" id="pd-stage" class="pill ${PD_STAGE_CLS[stageNow] || 'p-grey-cool'} pd4-stage" aria-haspopup="menu" aria-label="Stage: ${escapeHtml(stageText(stageNow))}. Move it on">${escapeHtml(stageText(stageNow))}${navIcon('chevronDown', 12)}</button>`
    : `<span class="pill ${PD_STAGE_CLS[stageNow] || 'p-grey-cool'} pd4-stage">${escapeHtml(stageNow === 'On Hold' ? 'On hold' : stageText(stageNow))}</span>`;
  const metaBits = [
    (project.title || project.description) ? escapeHtml(project.title || project.description) : (project.service ? escapeHtml(project.service) : ''),
    scheme ? escapeHtml(schemeLabel(scheme)) : '',
    // v4.19.4: a reviewer changes the lead right here (Karan 28/10/2026); the
    // picker also carries "See <name>'s projects", which the chip used to do.
    isReviewer
      ? (leadName
        ? `Lead <button type="button" id="lead-edit" class="pd4-edit pd4-lead" aria-haspopup="dialog" aria-label="${escapeHtml(`Lead: ${leadName}. Change the lead`)}">${escapeHtml(leadName)}</button>`
        : '<button type="button" id="lead-edit" class="pd4-edit is-empty" aria-haspopup="dialog" aria-label="No lead. Choose a lead">No lead</button>')
      : (leadName ? `Lead <button type="button" id="lead-chip" class="pd4-lead">${escapeHtml(leadName)}</button>` : 'No lead'),
  ].filter(Boolean);

  // The four figures: work and money on separate figures, role-gated as before.
  const daysTo = daysToDeadline;
  const statFig = (label, valueHtml, noteHtml, { id = '', muted = false, late = false } = {}) => `
    <div class="fig pd4-fig"${id ? ` id="${id}" role="button" tabindex="0"` : ''}>
      <div class="fig-label">${escapeHtml(label)}</div>
      <div class="fig-value${late ? ' late' : ''}${muted ? ' pd4-muted' : ''}">${valueHtml}</div>
      <div class="fig-note">${noteHtml}</div>
    </div>`;
  const stats = [];
  stats.push(statFig('Deadline', (dueVal && isReviewer)
    ? `<button type="button" id="due-edit" class="pd4-edit" aria-haspopup="dialog" aria-label="${escapeHtml(`Deadline ${PX_day(dueVal)}. Change it`)}">${escapeHtml(PX_day(dueVal))}</button>`
    : escapeHtml(dueVal ? PX_day(dueVal) : '—'),
    !dueVal ? (isReviewer ? '<button type="button" id="kpi-due" class="pd4-link">Set a deadline</button>' : 'not set')
      : ['Issued', 'Complete'].includes(project.status) ? 'project issued'
      : lateLive ? `<span class="late-text strong">${lateWord(overdueDays)} overdue</span>`
      : daysTo === 0 ? 'today' : daysTo === 1 ? 'tomorrow' : `in ${daysTo} days`,
    { late: lateLive }));
  if (isMoney) {
    stats.push(statFig('Fee', escapeHtml(feeRecorded && fee ? money(fee) : '—'),
      feeRecorded
        ? escapeHtml(`${money(invoicedTotal)} invoiced · ${money(Math.max(0, (fee || 0) - invoicedTotal))} to bill`)
        : '<button type="button" id="kpi-fee" class="pd4-link">Record the fee</button>'));
  } else {
    stats.push(statFig('Budget', escapeHtml(budgetHrs ? `${budgetHrs}h` : '—'), 'planned hours'));
  }
  stats.push(statFig('Time logged', escapeHtml(timeFigVal),
    escapeHtml(timePending ? 'still arriving' : budgetHrs ? `of a ${budgetHrs}h budget` : 'no budget set'),
    { id: (trackedMins && !timePending) ? 'open-time' : '', muted: !trackedMins || timePending }));
  if (isFinance) {
    stats.push(statFig('True cost',
      costUnlocked ? secretFig(trueCost ? money(trueCost) : '—', isFinance) : '<span class="secret-dots">••••</span>',
      costUnlocked ? 'floor at break-even' : 'floor at break-even · <button type="button" class="pd4-link" data-cost-unlock title="Cost rates need Face ID, Touch ID or your device PIN">Unlock</button>',
      { muted: !costUnlocked }));
  } else if (isMoney) {
    stats.push(statFig('Invoiced', escapeHtml(invoicedTotal ? money(invoicedTotal) : '—'), invoicedTotal ? 'to date' : 'nothing invoiced yet'));
  } else {
    stats.push(statFig('Issued', escapeHtml(issued.length ? String(issued.length) : '—'), issued.length ? 'in the register' : 'nothing issued'));
  }

  // ONE ALERT, the most important problem, with its fix as a button (p85:
  // "One alert per screen"). Priority: on hold › invoiced without an
  // instruction › no instruction › late › no fee › no deadline.
  const leadFirstName = String(leadName || '').split(' ')[0];
  const alert = (() => {
    if (project.status === 'On Hold') {
      return { b: `On hold: ${project.heldReason || 'no reason recorded'}.`, t: `It comes off hold by itself when time is logged or an instruction is added${project.statusBeforeHold ? `, and returns to ${stageText(project.statusBeforeHold)}` : ''}.` };
    }
    if (!instructions.length && isMoney && invoicedTotal > 0) {
      return { b: `${money(invoicedTotal)} has been invoiced, but the project has no instruction.`, t: 'Time and invoices need an instruction to sit against. Add the original scope so the money and the work line up.', act: isReviewer ? ['add-original', 'Add original scope'] : null };
    }
    if (!instructions.length) {
      return { b: 'This project has no instruction.', t: 'Time is logged against an instruction, so nobody can book hours here until it has one. Add the original scope.', act: isReviewer ? ['add-original', 'Add original scope'] : null };
    }
    if (lateLive) {
      return { b: `${lateWord(overdueDays)} past its ${PX_day(dueVal)} deadline.`, t: `It is at ${stageText(project.status)}${leadFirstName ? ` with ${leadFirstName}` : ''}. ${project.status === 'Review' ? 'Chase the check' : `Chase ${leadFirstName || 'the work'}`} or agree a new date with the client.`, act: isReviewer ? ['agree-date', 'Agree a new date'] : null };
    }
    if (isMoney && !feeRecorded) {
      return { b: 'No fee is recorded on this project.', t: proposedFee ? `The enquiry proposed ${money(proposedFee)}. Until a fee is recorded, cost, margin and invoicing stay blocked.` : 'Until a fee is recorded, cost, margin and invoicing stay blocked.', act: ['record-fee', 'Record fee'] };
    }
    if (isReviewer && !dueVal && !['Issued', 'Complete'].includes(project.status)) {
      return { b: 'No deadline is set.', t: 'Without one, Track cannot tell you when this project is running late.', act: ['set-deadline', 'Set a deadline'] };
    }
    return null;
  })();

  // The golden thread, compact: seven links, a bar over each. Blue = linked,
  // orange = the break (a missing link with later links already made, or a
  // late deadline), grey = not yet.
  const GT_LABEL = { ENQUIRY: 'Enquiry', FEE: 'Fee', INSTRUCTED: 'Instructed', 'WORKED ON': 'Worked on', DEADLINE: 'Deadline', ISSUED: 'Issued', INVOICED: 'Invoiced' };
  const laterDone = (i) => rawNodes.slice(i + 1).some((n) => n.state === 'done');
  const gtTone = (n, i) => n.state === 'done' ? 'on' : (n.state === 'broken' || n.state === 'late' || (i === firstBreak && laterDone(i))) ? 'brk' : 'off';
  const gtValue = (n) => {
    if (n.label === 'ENQUIRY') return preTrack ? 'Before Track' : (enqNo || (enqData ? 'Linked' : 'Not linked'));
    if (n.label === 'FEE') return feeRecorded ? (isMoney ? money(fee) : '••••') : 'Not recorded';
    if (n.label === 'INSTRUCTED') return nInstr ? PX_day((instr1 && (instr1.instructedAt || instr1.createdAt)) || project.createdAt) || `${nInstr}` : 'Not yet';
    if (n.label === 'WORKED ON') return timePending ? '…' : (trackedMins ? hoursHM(trackedMins / 60) : 'No time');
    if (n.label === 'DEADLINE') return dueVal ? PX_day(dueVal) : 'Not set';
    if (n.label === 'ISSUED') return issued.length ? `${issued.length} issued` : 'Nothing yet';
    if (n.label === 'INVOICED') return canFinance ? (invoicedTotal ? money(invoicedTotal) : 'Nothing yet') : '••••';
    return String(n.value);
  };
  const gtLinked = rawNodes.filter((n) => n.state === 'done').length;
  const gtHtml = rawNodes.map((n, i) => {
    const tone = gtTone(n, i);
    return `<button type="button" class="pd4-gt-seg" data-gt="${escapeHtml(n.label)}" title="${escapeHtml(n.record || '')}">
      <span class="pd4-gt-bar ${tone} draws"></span>
      <span class="pd4-gt-l">${escapeHtml(GT_LABEL[n.label] || n.label)}</span>
      <span class="pd4-gt-v ${tone}">${escapeHtml(gtValue(n))}</span>
    </button>`;
  }).join('');

  mount.innerHTML = `
    <div class="pdx-page pd4">
      <nav class="crumbs pd4-crumbs" aria-label="Breadcrumb"><button type="button" id="pd-back">${escapeHtml(backLabel || 'Projects')}</button><span aria-hidden="true">/</span><span>${escapeHtml(project.number || '—')}</span></nav>

      <header class="pd4-head${known ? '' : ' arrive-head'}">
        <div class="pd4-head-l">
          <h1 class="pd4-title"><span class="pd4-no">${escapeHtml(project.number || '—')}</span><span class="pd4-client">${escapeHtml(clientName)}</span>${stagePill}</h1>
          <div class="pd4-meta">${metaBits.join('<span class="pd4-sep"> · </span>')}<span id="scb-mast-host"></span></div>
        </div>
        <div class="pd4-acts">
          <button type="button" id="site-visit" class="btn">Site visit</button>
          ${canReports(session && session.email) ? '<button type="button" id="act-report" class="btn">Report</button>' : ''}
          <button type="button" id="act-docissue" class="btn" title="Before issue: check the files, then the issue register">Issue</button>
          ${isMoney ? '<button type="button" id="act-invoice" class="btn">Raise invoice</button>' : ''}
          <button type="button" id="act-time" class="btn primary">Log time</button>
          <button type="button" id="pd-more" class="btn icon pd4-more" aria-label="More actions" aria-haspopup="menu">⋯</button>
          <span hidden>${editPatterns ? '<button id="edit-project-panel">Edit details</button>' : ''}${isReviewer ? '<button id="edit-project">Edit details</button>' : ''}</span>
        </div>
      </header>

      <div class="figs pd4-figs">${stats.join('')}</div>

    ${suggestions.map((s) => `<div class="alert-v4 pd4-alert" data-sid="${escapeHtml(s.id)}">
      <span class="alert-text"><b>Track detected an issue — mark this project ${escapeHtml(stageText(s.suggested))}?</b>${s.needsConfirm ? ` ${escapeHtml(s.confidence || 'low')} confidence, so please confirm.` : ''}</span>
      <span class="pd4-alert-acts"><button class="btn sm primary" data-confirm="${escapeHtml(s.id)}">Confirm</button><button class="btn sm" data-dismiss="${escapeHtml(s.id)}">Dismiss</button></span></div>`).join('')}
    ${(isReviewer && project.status === 'Review' && reviewStaleDays(project) >= 2) ? `
      <div class="alert-v4 pd4-alert" id="review-stale">
        <span class="alert-text"><b>At Review for ${reviewStaleDays(project)} days.</b> Has it been issued?</span>
        <span class="pd4-alert-acts"><button class="btn sm primary" data-rev="Issued">Issued</button><button class="btn sm" data-rev="Part Issued">Part issued</button><button class="btn sm" data-rev="dismiss">Not yet</button></span></div>` : ''}
    ${alert ? `<div class="alert-v4 pd4-alert" id="pd-alert">
        <span class="alert-text"><b>${escapeHtml(alert.b)}</b> ${escapeHtml(alert.t)}</span>
        ${alert.act ? `<span class="pd4-alert-acts"><button type="button" class="btn primary" data-alert="${alert.act[0]}">${escapeHtml(alert.act[1])}</button></span>` : ''}
      </div>` : ''}

      <div id="pd-tabs" class="tabs pd4-tabs" role="tablist">
        <button class="pd-tab active" data-tab="overview" role="tab" aria-selected="true">Overview</button>
        <button class="pd-tab" data-tab="documents" role="tab" aria-selected="false">Documents<span class="n" id="pd-doc-count">${docCount ? ` ${docCount}` : ''}</span></button>
        <button class="pd-tab" data-tab="emails" role="tab" aria-selected="false">Correspondence<span class="n" id="pd-email-count"></span></button>
        <button class="pd-tab" data-tab="tasks" role="tab" aria-selected="false">Tasks<span class="n" id="pd-task-count"></span></button>
        ${isMoney ? '<button class="pd-tab" data-tab="finance" role="tab" aria-selected="false">Finance</button>' : ''}
      </div>

      <div data-panel="overview" class="pd4-ov">
        <div class="pd4-ov-l">
          ${(project.includedRevisions && Number(project.includedRevisions.count) > 0) ? `
          <p class="pd4-note-line"><b>Revisions:</b> ${escapeHtml(String(Number(project.includedRevisions.count)))} accommodated in the fee${Number(project.includedRevisions.hoursEach) ? `, ${hoursHM(Number(project.includedRevisions.hoursEach))} budgeted each` : ''}${project.includedRevisions.charged ? ' (charged on the quote)' : ''}. Beyond the allowance, further revisions are chargeable.</p>` : ''}

          <section class="pd4-sec">
            <div class="pd4-sec-h"><h2>Instructions</h2>${isReviewer ? '<button type="button" id="add-instr" class="pd4-link">+ Add instruction</button>' : ''}</div>
            <div id="instr-list"></div>
          </section>

          <section class="pd4-sec">
            <div class="pd4-sec-h"><h2>Golden thread <span class="pd4-sec-meta">enquiry to invoice · ${rawNodes.some((n) => n.pending) ? 'counting…' : `${gtLinked} of ${rawNodes.length} linked`}</span></h2></div>
            <div class="pd4-gt">${gtHtml}</div>
          </section>

          <section class="pd4-sec">
            <div class="pd4-sec-h"><h2>Activity</h2>${project.number ? '<button type="button" id="pd-note-add" class="pd4-link">+ Add a note</button>' : ''}</div>
            <div id="pd-note-form" class="pd4-note-form" hidden>
              <textarea id="pd-note-text" class="f-textarea" rows="2" placeholder="A note or a call — what do you want on the record?"></textarea>
              <div class="pd4-note-acts"><button type="button" id="pd-note-save" class="btn primary sm">Save note</button><button type="button" id="pd-note-cancel" class="btn sm">Cancel</button></div>
            </div>
            <div id="activity-feed" class="pd4-acts-list"><p class="pd4-none">Reading the record…</p></div>
          </section>

          <div id="pd-issues"></div>
          <div id="pd-feedback"></div>

          ${visits.length ? `<section class="pd4-sec">
            <div class="pd4-sec-h"><h2>Site visits</h2></div>
            <div>${visits.slice(0, 5).map((v) => {
              const rs = v.reportStatus === 'approved' ? { t: 'Report filed', c: '' } : v.reportStatus === 'draft' ? { t: 'Draft report', c: 'late-text' } : v.state === 'filed' ? { t: 'Create report', c: 'pd4-blue' } : { t: 'Pending sync', c: 'late-text' };
              return `<button class="site-visit-row pd4-visit" data-visit="${escapeHtml(v.id)}">
                <span class="pd4-visit-d">${escapeHtml(fmtDate(v.startedAt))}</span>
                <span class="pd4-visit-t">${escapeHtml(v.purpose || 'Site visit')} · ${escapeHtml(v.engineerName || v.engineerEmail || '')}</span>
                <span class="pd4-visit-s ${rs.c}">${rs.t}</span>
              </button>`; }).join('')}</div>
          </section>` : ''}
        </div>

        <aside class="pd4-ov-r">
          <section class="pd4-sec">
            <div class="pd4-sec-h"><h2>Next up</h2></div>
            <div id="pd-next"><p class="pd4-none">Reading the tasks…</p></div>
          </section>
          <section class="pd4-sec">
            <div class="pd4-sec-h"><h2>Details</h2></div>
            <dl id="pd-details" class="pd4-kv"></dl>
          </section>
          <!-- References (Karan 02/11/2026): planning, building control and the
               client's POs — more than one of each. project-refs.js. -->
          <section class="pd4-sec">
            <div class="pd4-sec-h"><h2>References</h2></div>
            <dl id="pd-refs" class="pd4-kv"></dl>
          </section>
          <!-- Site constraints (hand-off "Site checks", 15/10): once a check is on
               record its results live here; before that the Site row above
               carries the two links. Read after first paint. -->
          <div id="pdx-sc" class="scb-host" hidden></div>
        </aside>
      </div>

      <div data-panel="documents" hidden class="pd4-docs">
        <!-- Before issue (document control) — the "Issue" button lands here. -->
        <div class="pd4-before">
          <div id="pdx-dc">${PX.docControlHtml(PX.docControl(null), { number: project.number || '' })}</div>
          <div id="pdx-dc-open" class="pdx-dc-open" hidden>
            <div id="pdx-dc-fixes"></div>
            <div class="pdx-dc-reg">
              <span class="pdx-eyebrow">Register checks</span>
            <div id="dc-detail" style="display:flex;flex-direction:column;gap:12px">
              ${dcAudit.findings.length ? dcAudit.findings.slice(0, 8).map((f) => `<div style="display:flex;flex-direction:column;gap:3px">
                <span style="font-size:12.5px;font-weight:500;color:${f.level === 'blocked' ? 'var(--status-error)' : 'var(--brand-orange)'}">${escapeHtml(f.title)}</span>
                <span style="font-size:12px;line-height:1.5;color:var(--ink-2)">${escapeHtml(f.detail)}</span>
              </div>`).join('') : '<p class="g-note">Current register entries passed the available deterministic checks.</p>'}
              <span style="font-size:11.5px;line-height:1.5;color:var(--ink-3);padding-top:12px;border-top:1px solid var(--hair)">This advisory check does not issue, rename or supersede files.</span>
              ${dcAudit.findings.length && onAskTrack ? '<button class="btn ghost" id="dc-policy-check">Check against the house procedure</button>' : ''}
            </div>
            </div>
            <span class="pdx-note">Advisory only. Track never renames, issues or supersedes a file — it flags what does not match the house numbering.</span>
          </div>
        </div>
        <div class="pdx-sec">
          <div class="pd4-sec-h"><h2>Project folder <span class="pd4-sec-meta">opens in Track, with your Google permissions</span></h2>
            <a id="pdx-open-drive" class="btn sm" href="${escapeHtml(safeUrl(project.folderUrl || (project.folderId ? `https://drive.google.com/drive/folders/${project.folderId}` : 'https://drive.google.com/drive/shared-drives')))}" target="_blank" rel="noopener">Open in Drive</a>
          </div>
          <div id="pd-files"><p class="g-note">Reading the project folder…</p></div>
        </div>
        <div class="pd4-sec-h"><h2>Issued and received</h2></div>
        ${multiInstr ? `<div id="instr-filter" class="seg pd4-instr-filter">
          <button class="instr-chip active" data-instr="all">All</button>
          ${instructions.map((i) => `<button class="instr-chip" data-instr="${escapeHtml(String(i.seq))}">I${escapeHtml(String(i.seq))}</button>`).join('')}
        </div>` : ''}
        <div class="pd4-regs">
          <div class="pd4-reg">
            <div class="pd4-reg-h"><h3>Issued</h3>${issued.length ? '<button id="issue-sheet" class="btn sm">Issue sheet</button>' : ''}</div>
            <div id="issued-list">${issued.length ? issued.map(regRow).join('') : '<p class="pd4-none">Nothing registered yet.</p>'}</div>
            <p class="pd4-none">Issued attachments on project-tagged emails land here automatically.</p>
          </div>
          <div class="pd4-reg">
            <div class="pd4-reg-h"><h3>Received</h3>${received.length ? '<button id="receipt-sheet" class="btn sm">Receipt sheet</button>' : ''}</div>
            <div id="received-list">${received.length ? received.map(regRow).join('') : '<p class="pd4-none">Nothing registered yet.</p>'}</div>
            <p class="pd4-none">Attachments forwarded to the Track mailbox file into Documents Received and appear here.</p>
          </div>
        </div>
      </div>

      <div data-panel="emails" hidden>
      <!-- CORRESPONDENCE (17/10/2026, WhatsApp in Track board 1): the Emails tab
           renamed — email and WhatsApp in one timeline (views/wa-timeline.js). -->
      <div id="pd-emails-shared"></div>
    </div>

    <div data-panel="tasks" hidden>
      <div id="pd-tasks"></div>
    </div>

    ${isMoney ? `<div data-panel="finance" hidden>
      <div class="pd-fin">
        <div class="pd-fin-l">
          <div class="pd4-sec-h"><h2>Numbers</h2></div>
          <div class="pd-numbers">${numbers.map(([l, v]) => `<div><div class="pd-stat-label">${l}</div><div class="pd-stat-val">${(l === 'Due date' || l === 'Time tracked' || l === 'Margin' || l === 'True cost') ? v : escapeHtml(v)}</div></div>`).join('')}</div>
          ${PX.instructionFeesHtml(instructions)}
        </div>
        <div class="pd-fin-r">
          <div class="pd-team">
            <div class="pd4-sec-h"><h2>Invoices</h2></div>
            <div id="inv-list">
              ${invoices.length ? invoices.slice().sort((a, b) => invInstrSeq(a) - invInstrSeq(b) || String(a.number).localeCompare(String(b.number))).map((iv) => {
                const s = invInstrSeq(iv); const paid = iv.paymentStatus === 'Paid' || (iv.v2Payment && iv.v2Payment.status === 'Paid');
                return `<div class="pd-act-row" style="grid-template-columns:auto 1fr auto;align-items:center;gap:10px">
                  <div class="mono strong">INV ${escapeHtml(String(iv.number || '—'))}</div>
                  <div class="pd-act-desc">${escapeHtml(iv.description || '')}<span class="sec">${money(Number(iv.amount) || 0)} · ${escapeHtml(iv.paymentStatus || (paid ? 'Paid' : '—'))}</span></div>
                  <div style="white-space:nowrap;display:flex;align-items:center;gap:8px;font-size:13px;color:var(--ink)"><span class="sec" title="Instruction this invoice is on">#${escapeHtml(String(s))}</span>${isFinance && instructions.length > 1 ? `<button class="linkbtn inv-move" data-id="${escapeHtml(String(iv.id))}" data-seq="${escapeHtml(String(s))}" title="Move to another instruction">✎</button>` : ''}</div></div>`;
              }).join('') : '<p class="pd4-none">No invoices raised yet.</p>'}
            </div>
          </div>
        </div>
      </div>
    </div>` : ''}

    ${editPatterns ? `<section class="pd4-sec pd4-changes">
      <div class="pd4-sec-h"><h2>Changes to this record</h2></div>
      <div id="pd-changes"><p class="pd4-none">Loading…</p></div>
    </section>` : ''}

    </div>
`;

  // Delegated listeners hang on the page's OWN root, not on `mount`: a save
  // re-renders into the same mount, and listeners on it would stack up.
  const page = mount.querySelector('.pdx-page') || mount;

  // The breadcrumb's first crumb goes back the way the header's "← Projects" did.
  mount.querySelector('#pd-back')?.addEventListener('click', () => onBack && onBack());
  mount.querySelector('#act-time')?.addEventListener('click', () => requestLogTime({ projectId, projectNo: project.number || null }, () => goToView('time')));
  mount.querySelector('#act-invoice')?.addEventListener('click', () => goToView('invoices'));
  // "Issue" = before issue: the Documents tab, with the house-numbering check open.
  mount.querySelector('#act-docissue')?.addEventListener('click', () => {
    mount.querySelector('.pd-tab[data-tab="documents"]')?.click();
    const box = mount.querySelector('#pdx-dc-open');
    if (box && box.hidden) mount.querySelector('#pdx-dc-row')?.click();
    mount.querySelector('.pd4-before')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  // THE STAGE PILL (§11): forward only (D2) — past stages are disabled in the
  // picker. A move writes the status and one line on the record's change list.
  mount.querySelector('#pd-stage')?.addEventListener('click', (ev) => {
    openStagePicker(ev.currentTarget, {
      number: project.number, status: project.status,
      onPick: async (v) => {
        const from = project.status || null; const at = new Date().toISOString();
        try {
          await updateProjectFields(projectId, { status: v, statusUpdatedAt: at, statusSource: `manual (${session.email})`, updatedBy: session.email, updatedAt: at });
          addRecordChange({ record: 'projects/' + projectId, field: 'status', label: 'Status', from, to: v, by: session.email, byName: session.displayName || null, at, money: false }).catch(() => {});
          project.status = v;
          reRender();
        } catch (e) { alert('Couldn’t move the stage: ' + (e.message || e)); }
      },
    });
  });

  // A redraw nobody asked for must not replay the entrance animation — the card
  // would rise and fade a second after you opened it, which reads as a glitch.
  // Dropped again shortly after, so ordinary interactions still animate.
  if (quiet) {
    mount.classList.add('pd-quiet');
    setTimeout(() => mount.classList.remove('pd-quiet'), 800);
  }

  // The Time-tracked figure carries id="open-time" in the markup (only when time
  // is logged); add keyboard semantics so Enter/Space opens the breakdown.
  // True cost: the unlock (17/10) — verify, then draw the page again with the rates.
  mount.querySelector('[data-cost-unlock]')?.addEventListener('click', async () => {
    if (await ensureStepUp()) renderProjectDetail(mount, projectId, session, onBack, { backLabel, onOpenLead, onOpenEnquiry, onOpenClient, onAskTrack, quiet: true });
  });
  const timeFigEl = mount.querySelector('#open-time');
  if (timeFigEl) {
    timeFigEl.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); timeFigEl.click(); } });
  }

  // Tabs (design handoff, Karan 04/08) — all panels are in the DOM; we toggle
  // visibility so every existing handler still binds. Active state is inline
  // (5b: font-weight + an inset navy underline) since the presentation is inline.
  mount.querySelectorAll('.pd-tab').forEach((t) => t.addEventListener('click', () => {
    const tab = t.dataset.tab;
    mount.querySelectorAll('.pd-tab').forEach((x) => {
      const on = x === t;
      x.classList.toggle('active', on);
      x.setAttribute('aria-selected', String(on));
    });
    mount.querySelectorAll('[data-panel]').forEach((p) => { p.hidden = p.dataset.panel !== tab; });
  }));
  // ── Tasks tab (design_handoff_tasks_v2, "Project record › Tasks") ────────
  // Loaded the first time the tab is opened rather than with the record: most
  // visits to a project never open it, and this is one more Firestore read.
  // By instruction (default) or Board; time against budget reads the same
  // timesheet rollup the record already built (minsByInstr). Fee burn is NOT
  // shown here — money belongs on Finance (hand-off).
  // Next up and the Tasks count need the project's tasks on arrival now, so the
  // one read is made at once (it used to wait for the tab).
  let openTaskRef = null, reloadTasksRef = null;
  let projTasks = null;
  const tasksHost = mount.querySelector('#pd-tasks');
  if (tasksHost) {
    const state = { tasks: null, mode: 'list', showDone: false, addInstr: undefined, note: '', updatedAt: '' };
    const team = () => teamFrom(Object.entries(users || {}).map(([id, u]) => ({ id, ...u })), [], dayKey());
    const names = Object.fromEntries(Object.entries(users || {}).map(([id, u]) => [id, titleCase(u.displayName || id)]));
    const find = (id) => (state.tasks || []).find((x) => x.id === id) || null;
    const paint = () => mountProjectTasks(tasksHost, {
      tasks: state.tasks || [], instructions, minsBySeq: minsByInstr, timePending,
      people: team(), names, projectNumber: project.number || '', today: dayKey(),
      mode: state.mode, showDone: state.showDone, addInstr: state.addInstr,
      note: state.note, updatedAt: state.updatedAt,
    }, handlers);
    const failed = (what) => (e) => {
      console.error('[project tasks]', e);
      state.note = `${what} did not save — ${(e && e.message) || e}`;
      return load(true);
    };

    const handlers = {
      // Optimistic: the row flips at once, the write follows, and a refused
      // write says so in the tab and puts the row back (by re-reading).
      onTick: async (id) => {
        const t = find(id);
        if (!t) return;
        state.note = '';
        const next = t.state === 'done' ? 'todo' : 'done';
        t.state = next;
        t.completedAtDay = next === 'done' ? dayKey() : null;
        paint();
        await setTaskState(id, next, session).catch(failed('That tick'));
      },
      onOpen: (id) => { const t = find(id); if (t) openTask(t); },
      onMode: (m) => { state.mode = m === 'board' ? 'board' : 'list'; paint(); },
      onShowDone: () => { state.showDone = !state.showDone; paint(); },
      onPickInstr: (seq) => { state.addInstr = seq; },
      // The add line files the task to this project and the picked instruction
      // (its SEQUENCE NUMBER as a string), assigned to whoever typed it.
      onAdd: async ({ title, instructionId }) => {
        const me = meIn(team(), session.email);
        state.note = '';
        try {
          await createTask({
            title, projectId: project.id || projectId, instructionId: instructionId ? String(instructionId) : null,
            assigneeId: me ? me.userId : null, assigneeEmail: me ? me.email : null,
          }, session);
        } catch (e) {
          await failed('The new task')(e);
          return false;
        }
        await load(true);
        return true;
      },
    };

    // The task panel and its actions are the Tasks page's (views/task-panel.js,
    // views/task-actions.js) — ONE implementation of what each button writes,
    // so the record and the page cannot drift. Loaded lazily: most visits to a
    // project never open a task.
    function openTask(task) {
      Promise.all([import('./task-panel.js'), import('./task-actions.js'), import('./task-new.js'), import('./calc-icon.js')])
        .then(([panel, acts, tn, nav]) => {
          const people = team();
          const me = meIn(people, session.email);
          const optimistic = (id, patch) => {
            const t = find(id);
            if (!t) return null;
            Object.assign(t, patch);
            paint();
            return t;
          };
          const { onAction } = acts.createTaskActions({
            session, find, people: () => people, optimistic, fail: failed,
          });
          panel.openTaskPanel({
            host: document.body, task: find(task.id) || task, people,
            project: { ...project, id: project.id || projectId }, tasks: state.tasks || [], meId: me ? me.userId : null,
            loadActivity: () => taskActivity(task.id).then((r) => r || []),
            onComment: (text) => addTaskActivity(task.id, { kind: 'comment', text }, session),
            onAction: (key, payload) => onAction(task.id, key, payload),
            onEdit: (t) => tn.openEditTask({
              task: { ...t, projectRef: project.number || '' }, people,
              projects: [{ id: project.id || projectId, number: project.number || '', name: project.clientName || project.title || '',
                site: (project.siteAddress && (project.siteAddress.line1 || project.siteAddress.postcode)) || '', status: project.status || '' }],
              onSave: async (patch) => { optimistic(t.id, patch); await updateTask(t.id, patch, session).catch(failed('The edit')); },
            }),
            onLogTime: () => nav.goToView('time'),
            onClose: () => load(true),
          });
        }).catch((e) => console.warn('[project tasks] the task panel did not open —', e && e.message));
    }

    async function load(force) {
      if (state.tasks && !force) return;
      state.tasks = await projectTasks(project.id || projectId).catch(() => []);
      state.updatedAt = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      const open = state.tasks.filter(isOpen).length;
      const badge = mount.querySelector('#pd-task-count');
      if (badge) badge.textContent = open ? ` ${open}` : '';
      projTasks = state.tasks;
      paint();
      paintNextUp();
      paintDetails();
    }
    openTaskRef = (id) => { const t = find(id); if (t) openTask(t); };
    reloadTasksRef = () => load(true);
    load();
  }

  // Document Control — collapsed row toggles the advisory detail.
  const dcRow = mount.querySelector('#dc-row');
  if (dcRow) dcRow.addEventListener('click', () => {
    const d = mount.querySelector('#dc-detail'), c = mount.querySelector('#dc-chev');
    if (d) { d.hidden = !d.hidden; if (c) c.textContent = d.hidden ? '▾' : '▴'; }
  });
  // Aliases + NEXT/KPI actions → reuse the canonical flows.
  mount.querySelector('#add-instr-2')?.addEventListener('click', () => mount.querySelector('#add-instr')?.click());
  const cl2 = mount.querySelector('#open-client-2');
  if (cl2 && onOpenClient && client) cl2.addEventListener('click', () => onOpenClient(client.id));
  const enq2 = mount.querySelector('#open-enquiry-2');
  if (enq2 && onOpenEnquiry && instr1 && instr1.enquiryRef) enq2.addEventListener('click', () => onOpenEnquiry(relPath(instr1.enquiryRef).split('/').pop()));
  mount.querySelector('#files-open-all')?.addEventListener('click', () => mount.querySelector('.pd-tab[data-tab="documents"]')?.click());
  mount.querySelector('#nx-due')?.addEventListener('click', () => mount.querySelector('#edit-due')?.click());
  mount.querySelector('#nx-fee')?.addEventListener('click', () => mount.querySelector('#add-instr')?.click());
  mount.querySelector('#kpi-logtime')?.addEventListener('click', () => { window.location.href = '/time'; });
  mount.querySelector('#kpi-budget')?.addEventListener('click', () => mount.querySelector('#add-instr')?.click());
  mount.querySelector('#thread-due')?.addEventListener('click', () => mount.querySelector('#edit-due')?.click());
  mount.querySelector('#act-issue')?.addEventListener('click', () => {
    mount.querySelector('.pd-tab[data-tab="overview"]')?.click();
    const addBtn = mount.querySelector('#iss-add');
    if (addBtn) addBtn.click(); else mount.querySelector('#pd-issues')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  // ── Drawing review via the Documents tab (Karan 08/08): clicking a register
  // file opens it IN TRACK — PDFs in the full-screen reviewer (with the
  // register's number, revision and status on the top edge), models and calcs
  // in their existing viewers. Pilot-gated; everyone else keeps the plain
  // Drive link. Superseded sheets open normally for now (§11.6 undecided;
  // markup is session-local so nothing can be recorded against them anyway).
  const myName = (() => {
    const me = Object.values(users).find((u) => String(u.email || '').toLowerCase() === String(session.email || '').toLowerCase());
    const fallback = String(session.email || '').split('@')[0].replace(/[._]+/g, ' ');
    return titleCase((me && me.displayName) || fallback || 'You');
  })();
  const reviewerFor = (d) => {
    const st = effStatus(d);
    return {
      reference: [d.docNumber, d.revision ? `Rev ${d.revision}` : null].filter(Boolean).join(' · ')
        || String(d.filename || '').replace(/\.[a-z0-9]+$/i, ''),
      title: String(d.filename || '').replace(/\.[a-z0-9]+$/i, ''),
      status: st === 'superseded' ? 'Superseded' : st === BINNED ? 'Binned' : st === 'draft' ? 'Draft' : 'Current',
      user: myName,
      email: session.email,
      projectNo: String(project.number || ''),
    };
  };
  mount.querySelectorAll('a.dr-doc').forEach((a) => a.addEventListener('click', (e) => {
    const d = documents.find((x) => x.id === a.dataset.doc);
    const fileId = d && drDriveId(d);
    if (!fileId) return;                       // no pointer → let the href do its thing
    e.preventDefault();
    // ← / → step through the documents in the order they are listed.
    const listed = [...mount.querySelectorAll('a.dr-doc')].map((x) => documents.find((y) => y.id === x.dataset.doc)).filter((y) => y && drDriveId(y));
    const asFile = (y) => ({ id: drDriveId(y), name: y.filename || '', webViewLink: y.driveUrl || '', __doc: y });
    openFile(asFile(d), { reviewer: reviewerFor(d), reviewerFor: (g) => reviewerFor(g.__doc), siblings: listed.map(asFile) });
  }));

  // ── The golden thread ─────────────────────────────────────────────────────
  // The panel reads; this page owns every side effect. A break's chase becomes
  // a real task with the break quoted in it, so the person who picks it up is
  // told what is missing rather than just that something is.
  const raiseFor = async (b) => {
    const list = Array.isArray(b) ? b : [b].filter(Boolean);
    if (!list.length) return;
    // "Task raised." was said whatever happened. A break marked as yours sent
    // the email without the id (refused by newTask), and every one was refused
    // by the rules as source 'system' — so nothing was ever raised.
    const me = meIn(teamFrom(Object.entries(users || {}).map(([id, u]) => ({ id, ...u })), [], dayKey()), session.email);
    let raised = 0;
    const failed = [];
    for (const brk of list) {
      try {
        await createTask({
          title: brk.title,
          description: `${brk.why}\n\nRaised from the golden thread on ${project.number}.`,
          projectId,
          assigneeId: brk.mine && me ? me.userId : null,
          assigneeEmail: brk.mine && me ? me.email : null,
          estimateHours: 0.5,
          source: 'system', sourceLabel: 'GOLDEN THREAD', sourceRef: `projects/${projectId}`,
        }, session);
        raised += 1;
      } catch (e) {
        console.warn('[golden thread] could not raise the task —', e.message);
        failed.push(e.message);
      }
    }
    alert(failed.length
      ? `${raised} of ${list.length} raised. Not raised: ${failed[0]}`
      : (raised === 1 ? 'Task raised.' : `${raised} tasks raised.`));
  };
  // The latest site-constraints snapshot, once the card has read it, joins the
  // thread's evidence as ONE read-only record line (brief §9) so "is this site
  // in a coal high risk area?" can be answered from the record and cite it.
  let siteCheckLatest = null;
  const openGT = () => openProjectThread({
    project: { ...project, id: projectId }, client, session, isMoney, source: gtSource,
    extraRecords: () => { const r = siteCheckEvidence(siteCheckLatest); return r ? [r] : []; },
    on: {
      raise: raiseFor,
      write: () => mount.querySelector('.pd-tab[data-tab="notes"]')?.click(),
      openTask: (id) => { if (navOpts.onOpenTask) navOpts.onOpenTask(id); else location.href = '/tasks'; },
      openRecord: (n) => {
        if (n && n.kind === 'enquiry' && onOpenEnquiry && n.ref) onOpenEnquiry(n.ref);
        else if (n && n.kind === 'invoice') location.href = '/invoices';
      },
    },
  });
  mount.querySelector('#golden-open')?.addEventListener('click', openGT);
  mount.querySelector('#kpi-thread')?.addEventListener('click', openGT);
  mount.querySelector('#chase-lead')?.addEventListener('click', () => mount.querySelector('.pd-tab[data-tab="emails"]')?.click());

  // Record fee (the golden-thread flow) — writes a project-level fee. Prefilled
  // from the enquiry's proposed fee when there is one (Karan 04/08).
  // The fee record does not exist until it is recorded, and the figure on the
  // card behind is exactly what is being replaced — so Record fee stays a MODAL
  // (Dialogs & Forms §1, 7a surface test), not a panel. 520.
  // Set the fee / Revise the fee on one instruction (v4.20.0; views/instruction-fee.js).
  function feeAction(kind, instr) {
    const seq = Number(instr.seq) || 1;
    const ctx = {
      project: { ...project, id: projectId }, projectId, instr, instructions, documents,
      enquiry: seq === 1 ? enqData : null, enquiryNos: enqData && enqData.number ? [String(enqData.number)] : [],
      invoicedNet: invByInstr[seq] || 0,
      onDone: () => renderProjectDetail(mount, projectId, session, onBack, navOpts),
    };
    if (kind === 'revise') openReviseFee(ctx); else openSetFee(ctx);
  }
  const recordFee = () => {
    // v4.20.0: the fee belongs on the INSTRUCTION (the billable unit) — Record
    // fee sets it on the first instruction with none, instruction 1 included.
    // The project-level record stays only for a project with no instructions.
    const bare = instructions.slice().sort((a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0)).find((i) => !(Number(i.fee) > 0));
    if (isReviewer && bare) { feeAction('set', bare); return; }
    openDialog({
      title: 'Record fee',
      subtitle: `${project.number} · ${clientName}`,
      width: 520,
      note: proposedFee
        ? `Prefilled from the enquiry’s proposal (${money(proposedFee)}) — recording it repairs the fee link.`
        : 'The golden-thread fee link',
      fields: [
        {
          key: 'fee', label: 'Agreed fee, ex VAT', type: 'money', affix: '£', span: true,
          value: proposedFee ? Number(proposedFee) : '', placeholder: 'e.g. 3400', required: true,
          validate: (v) => (Number(v) > 0 ? null : 'Enter the agreed fee.'),
        },
      ],
      primary: {
        label: 'Record fee',
        onSubmit: async (v) => {
          await updateProjectFields(projectId, { fee: Number(v.fee), feeSource: proposedFee ? 'enquiry-proposal' : 'manual', updatedBy: session.email, updatedAt: new Date().toISOString() });
          renderProjectDetail(mount, projectId, session, onBack, navOpts);
        },
      },
    });
  };
  mount.querySelector('#thread-fee')?.addEventListener('click', recordFee);
  mount.querySelector('#kpi-fee')?.addEventListener('click', recordFee);

  // Calculate (Karan-only, 05/10): this project's calc packages.
  mount.querySelector('#act-calc')?.addEventListener('click', () => goToCalculate(`project-${projectId}`));
  // Reports phase B1 (28/10/2026): the on-site report, on a tablet or a PC (feature + list gated).
  mount.querySelector('#act-report')?.addEventListener('click', () => {
    import('./report-capture-host.js').then((m) => m.openReportCapture({ project: { ...project, id: projectId, clientName }, session }))
      .catch((e) => alert(e.message || String(e)));
  });
  mount.querySelector('#site-visit')?.addEventListener('click', async () => {
    try {
      await openSiteVisit({ project: { ...project, id: projectId, clientName }, session, onComplete: () => renderProjectDetail(mount, projectId, session, onBack, navOpts) });
    } catch (e) { alert(e.message || String(e)); }
  });
  mount.querySelectorAll('.site-visit-row').forEach((row) => row.addEventListener('click', () => {
    const visit = visits.find((v) => v.id === row.dataset.visit);
    if (visit) openSiteReport({ project: { ...project, id: projectId, clientName }, visit, session, onChanged: () => renderProjectDetail(mount, projectId, session, onBack, navOpts) });
  }));

  const leadChip = mount.querySelector('#lead-chip');
  if (leadChip && onOpenLead) leadChip.addEventListener('click', () => onOpenLead(leadId, leadName));
  const clientLink = mount.querySelector('#open-client');
  if (clientLink && onOpenClient && client) clientLink.addEventListener('click', () => onOpenClient(client.id));
  const enqLink = mount.querySelector('#open-enquiry');
  if (enqLink && onOpenEnquiry && instr1 && instr1.enquiryRef) enqLink.addEventListener('click', () => onOpenEnquiry(relPath(instr1.enquiryRef).split('/').pop()));
  const dcPolicy = mount.querySelector('#dc-policy-check');
  if (dcPolicy && onAskTrack) dcPolicy.addEventListener('click', () => onAskTrack(policyQuestion(project, dcAudit)));

  const sheetBtn = (sel, kind, docs) => {
    const b = mount.querySelector(sel);
    if (b) b.addEventListener('click', () => openDocSheet({
      kind, project, clientName, docs, generatedBy: session.displayName,
    }));
  };
  sheetBtn('#issue-sheet', 'issue', issued);
  sheetBtn('#receipt-sheet', 'receipt', received);

  // Re-classify a document Issued ↔ Received when the auto-detector got it wrong
  // (Karan 21/07 — received drawings that sat in an @asp reply were tagged Issued).
  mount.querySelectorAll('.doc-flip').forEach((b) => b.addEventListener('click', async () => {
    if (!isReviewer) return;
    b.disabled = true;
    try {
      await updateDocument(b.dataset.id, { direction: b.dataset.dir, updatedBy: session.email, updatedAt: new Date().toISOString() });
      renderProjectDetail(mount, projectId, session, onBack, navOpts);
    } catch (e) { b.disabled = false; alert('Couldn’t move it: ' + (e.message || e)); }
  }));

  // Instruction lens (Karan 28/07): filter the Received/Issued docs to one instruction.
  mount.querySelectorAll('.instr-chip').forEach((c) => c.addEventListener('click', () => {
    const sel = c.dataset.instr;
    mount.querySelectorAll('.instr-chip').forEach((x) => {
      const on = x === c;
      x.classList.toggle('active', on);
    });
    // doc-rows are display:grid — restore grid (not '') so the row layout holds.
    mount.querySelectorAll('.doc-row').forEach((row) => { row.style.display = (sel === 'all' || row.dataset.instr === sel) ? 'grid' : 'none'; });
  }));
  // Manually tag a document to an instruction.
  // DIALOGS&FORMS Q: single-choice row-picker on an existing document (item-9
  // sweep). Not a multi-field form, so left on openOverlay; the inline-select /
  // enum-pill (6e) is its natural home in a later pass — not converted to avoid
  // inventing a surface the artboards don't draw.
  mount.querySelectorAll('.doc-instr').forEach((b) => b.addEventListener('click', () => {
    if (!isReviewer) return;
    const cur = Number(b.dataset.seq) || 1;
    const { content, close } = openOverlay({ title: 'Tag document to an instruction', subtitle: 'Which instruction is this document for?' });
    content.innerHTML = `<div style="padding:12px 16px 16px">${instructions.map((i) => `
      <div class="ct-row doc-pick" style="grid-template-columns:44px 1fr auto;padding:11px 12px;cursor:pointer" data-seq="${escapeHtml(String(i.seq))}">
        <span class="mono strong">#${escapeHtml(String(i.seq))}</span>
        <span>${escapeHtml(i.description || 'Instruction')}${i.status ? ` <span class="sec">· ${escapeHtml(i.status)}</span>` : ''}</span>
        <span>${Number(i.seq) === cur ? '<span class="sec">current</span>' : ''}</span></div>`).join('')}</div>`;
    content.querySelectorAll('.doc-pick').forEach((r) => r.addEventListener('click', async () => {
      const seq = Number(r.dataset.seq);
      if (seq === cur) { close(); return; }
      try {
        await updateDocument(b.dataset.id, { instructionSeq: seq, updatedBy: session.email, updatedAt: new Date().toISOString() });
        close(); renderProjectDetail(mount, projectId, session, onBack, navOpts);
      } catch (e) { alert('Couldn’t tag it: ' + (e.message || e)); }
    }));
  }));
  // Resolve document — a multi-field edit of an existing document record, so a
  // PANEL, not a modal (Dialogs & Forms item-9 sweep).
  mount.querySelectorAll('.doc-review').forEach((b) => b.addEventListener('click', () => {
    if (!isReviewer) return;
    const d = documents.find((x) => x.id === b.dataset.id);
    if (!d) return;
    let dNo, dRev, dCat, dStatus;
    openPanel({
      eyebrow: 'Resolve document',
      title: d.filename || 'Document',
      consequence: 'Fixing the number/revision updates the register only — Drive moves for supersession stay with the track@ agent.',
      render(body, { field }) {
        dNo = field({ key: 'no', label: 'Doc number', type: 'text', mono: true, value: d.docNumber || '', placeholder: 'e.g. 8557-05-0401' });
        dRev = field({ key: 'rev', label: 'Revision', type: 'text', mono: true, value: d.revision || '', placeholder: 'e.g. P2' });
        dCat = field({ key: 'cat', label: 'Category', type: 'select', value: d.category || 'drawing', options: ['drawing', 'report', 'image', 'other'].map((c) => ({ value: c, label: c })) });
        dStatus = field({ key: 'status', label: 'Status', type: 'select', value: d.status === 'superseded' ? 'superseded' : d.status === BINNED ? BINNED : 'current', options: [{ value: 'current', label: 'Current' }, { value: 'superseded', label: 'Superseded' }, ...(d.status === BINNED ? [{ value: BINNED, label: 'Binned (file in Drive’s bin)' }] : [])] });
        const grid = document.createElement('div');
        grid.className = 'tf-grid';
        [dNo, dRev, dCat, dStatus].forEach((c) => grid.appendChild(c.el));
        body.appendChild(grid);
      },
      primary: {
        label: 'Resolve document',
        onSubmit: async () => {
          await updateDocument(d.id, {
            docNumber: dNo.get() || null,
            revision: dRev.get() || null,
            category: dCat.get(),
            status: dStatus.get(),
            needsReview: false,
            reviewedBy: session.email, updatedAt: new Date().toISOString(),
          });
          renderProjectDetail(mount, projectId, session, onBack, navOpts);
        },
      },
    });
  }));

  // Manual edit of the project's descriptive fields (Karan 14/07 — Sanj couldn't
  // fix a typo; also the way to clean up V1-synced junk like 6574's bad address).
  const editBtn = mount.querySelector('#edit-project');
  if (editBtn) editBtn.addEventListener('click', () => {
    // Edits a project that EXISTS and is named in the 7a panel list ("Edit
    // project details") — so a PANEL, not the old modal (Dialogs & Forms item-9
    // sweep). Site keeps its Places autocomplete via the render() escape hatch.
    const site0 = (project.siteAddress && project.siteAddress.line1) || '';
    let pTitle, pScheme, pService, pSite, peSite;
    let pendingDialogClient = null;
    openPanel({
      eyebrow: 'Edit project',
      title: 'Edit project details',
      subtitle: `${project.number} · ${clientName}`,
      lastEdited: project.updatedAt ? `Last edited ${fmtDate(project.updatedAt)}` : '',
      consequence: canChangeClient
        ? 'Corrects the client, title, scheme, type and site — including tidying anything that came across wrong from the old system. Invoices already raised stay with the client they went to.'
        : 'Corrects title, scheme, type and site — including tidying anything that came across wrong from the old system.',
      render(body, { field }) {
        if (canChangeClient) {
          // The client (v4.6.2): shown, and changed through the picker — never a
          // free-text field, because it is a LINK to a client record.
          const row = document.createElement('div');
          row.className = 'tf-field';
          row.setAttribute('style', 'display:flex;flex-direction:column;gap:6px;margin-bottom:18px');
          row.innerHTML = `<span class="tf-label" style="font-size:13px;color:var(--ink-2)">Client</span>
            <div data-pd-client-box style="display:flex;align-items:center;gap:10px;min-height:40px;border:1px solid var(--hair-strong);border-radius:8px;padding:0 12px;background:var(--surface)">
              <span data-pd-client style="flex:1;min-width:0;font-size:14px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escapeHtml(shownClient || 'No client')}</span>
              <button type="button" class="pd4-link" data-pd-client-change style="font-size:13px">Change…</button>
            </div>
            <span data-pd-client-note style="font-size:12px;color:var(--ink-3)" hidden></span>`;
          body.appendChild(row);
          wireClientField(row, (pick) => { pendingDialogClient = pick; });
        }
        pTitle = field({ key: 'title', label: 'Project title / description', type: 'textarea', span: true, value: project.title || project.description || '', placeholder: 'e.g. New Coaster & Building — Ice Mountain' });
        pScheme = field({ key: 'scheme', label: 'Scheme', type: 'text', value: project.scheme || '', placeholder: 'e.g. Amusement / Residential' });
        const svcCur = (project.service || '').trim();
        const svcOpts = PROJECT_TYPES.slice();
        if (svcCur && !svcOpts.some((o) => o.toLowerCase() === svcCur.toLowerCase())) svcOpts.push(svcCur);
        pService = field({ key: 'service', label: 'Type', type: 'select', value: svcCur, placeholder: '— Select type —', options: svcOpts.map((o) => ({ value: o, label: o })) });
        pSite = field({ key: 'site', label: 'Site address', type: 'text', span: true, value: site0, placeholder: 'e.g. Fantasy Island, Ingoldmells, PE25 1RH, UK' });
        const grid = document.createElement('div');
        grid.className = 'tf-grid';
        [pTitle, pScheme, pService, pSite].forEach((c) => grid.appendChild(c.el));
        body.appendChild(grid);
        // Places autocomplete needs its own dropdown anchor next to the input.
        const drop = document.createElement('div'); drop.className = 'fc-dropdown'; drop.hidden = true;
        pSite.el.appendChild(drop);
        peSite = attachPlacesAutocomplete(pSite.el.querySelector('input'), drop, { initial: { lat: project.siteLat, lng: project.siteLng, placeId: project.sitePlaceId } });
      },
      primary: {
        label: 'Save details',
        onSubmit: async () => {
          // A client change is confirmed first, and written on its own (server
          // side); the details below are saved only once it has gone through.
          if (pendingDialogClient && !(await confirmClientChange(pendingDialogClient))) {
            throw new Error('Nothing saved — the client change was cancelled.');
          }
          pendingDialogClient = null;
          const site = pSite.get();
          await updateProjectFields(projectId, {
            title: pTitle.get() || null,
            description: pTitle.get() || null,
            scheme: pScheme.get() || null,
            service: pService.get() || null,
            siteAddress: site ? { line1: site } : null,
            site: site || null,
            siteLat: peSite.meta.lat, siteLng: peSite.meta.lng, sitePlaceId: peSite.meta.placeId,
            manualOverride: true,
            updatedBy: session.email, updatedAt: new Date().toISOString(),
          });
          renderProjectDetail(mount, projectId, session, onBack, navOpts);
        },
      },
    });
  });

  // Time-tracked breakdown (Karan 14/07, rebuilt as a full page): clicking the
  // figure opens a dedicated page — finance summary (break-even cost + margin,
  // Finance only), a per-person split, then every timesheet entry with its note.
  // Its own "← Back to project" returns HERE (not to the Projects list).
  const showTimeBreakdown = () => {
    const rev = fee || invoicedTotal || 0;
    const marginPct = (trueCost && rev) ? Math.round(((rev - trueCost) / rev) * 100) : null;
    const trainMins = timeEntries.reduce((n, e) => n + (e.training ? e.mins : 0), 0);
    const nameFor = (key, fallback) => (usersMap[key] && usersMap[key].displayName) || fallback || titleCase(key);

    // Per-person totals — complete, so they include pre-launch imported time.
    const people = Object.entries(minsByUser)
      .map(([key, mins]) => ({ key, mins, cost: (mins / 60) * (Number(costRates[key]) || 0), rated: !!Number(costRates[key]) }))
      .sort((a, b) => b.mins - a.mins);

    const stats = [['Total time', hoursHM(trackedMins / 60)]];
    if (isFinance) stats.push(['Break-even cost', secretFig(trueCost != null ? money(trueCost) : '—', isFinance)]);
    if (isFinance) stats.push(['Margin', secretFig(marginPct != null ? `${marginPct}% <span class="g-note" style="font-size:12px">(${money(rev - trueCost)})</span>` : '—', isFinance)]);
    if (isMoney) stats.push(['Fee', fee ? money(fee) : '—']);
    if (isMoney) stats.push(['Invoiced', invoicedTotal ? money(invoicedTotal) : '—']);

    const rows = timeEntries.slice().sort((a, b) =>
      String(b.weekStart || '').localeCompare(String(a.weekStart || '')) || String(a.who).localeCompare(String(b.who)));

    mount.innerHTML = `
      <nav class="crumbs" aria-label="Breadcrumb"><button type="button" id="tb-back-list">${escapeHtml(backLabel || 'Projects')}</button><span aria-hidden="true">/</span><button type="button" id="tb-back">${escapeHtml(project.number || 'Project')}</button><span aria-hidden="true">/</span><span>Time</span></nav>
      <div class="pd-head" style="margin-top:16px">
        <span class="pd-no">${escapeHtml(project.number || '—')}</span>
        <h1 class="pd-client">${escapeHtml(clientName)}</h1>
      </div>
      <div class="eyebrow blue" style="margin-top:2px">Time breakdown${project.title ? ` · ${escapeHtml(project.title)}` : ''}</div>

      <div class="pd-numbers tbk-stats">
        ${stats.map(([l, v]) => `<div><div class="pd-stat-label">${l}</div><div class="pd-stat-val">${v}</div></div>`).join('')}
      </div>

      <div class="eyebrow blue" style="margin-top:36px">By person</div>
      <div class="tbk-scroll"><table class="tbk-table">
        <thead><tr><th>Person</th><th class="r">Time</th><th class="r">Share</th>${isFinance ? '<th class="r">Break-even cost</th>' : ''}</tr></thead>
        <tbody>
          ${people.map((p) => `<tr>
            <td>${escapeHtml(nameFor(p.key))}</td>
            <td class="r mono">${hoursHM(p.mins / 60)}</td>
            <td class="r mono">${trackedMins ? Math.round((p.mins / trackedMins) * 100) : 0}%</td>
            ${isFinance ? `<td class="r mono">${p.rated ? secretFig(money(p.cost), isFinance) : '<span class="g-note">no rate set</span>'}</td>` : ''}
          </tr>`).join('') || `<tr><td colspan="${isFinance ? 4 : 3}" class="g-note">No time logged.</td></tr>`}
        </tbody>
      </table></div>

      <div class="eyebrow blue" style="margin-top:36px">All time allocations</div>
      <div class="tbk-scroll"><table class="tbk-table">
        <thead><tr><th>Person</th><th>Week</th><th>Task &amp; note</th><th class="r">Time</th>${isReviewer ? '<th></th>' : ''}</tr></thead>
        <tbody>
          ${rows.map((r) => `<tr data-sheet="${escapeHtml(r.sheetId || '')}" data-row="${r.rowIndex}">
            <td>${escapeHtml(r.who)}</td>
            <td class="mono" style="white-space:nowrap">${r.weekStart ? 'w/c ' + fmtDate(r.weekStart) : '—'}</td>
            <td><span class="tbk-task">${escapeHtml(r.task || '—')}</span> <span class="tbk-train-pill">${r.training ? pill('training', 'hold') : ''}</span>${r.note ? `<div class="g-note tbk-note">${escapeHtml(r.note)}</div>` : ''}</td>
            <td class="r mono">${hoursHM(r.mins / 60)}</td>
            ${isReviewer ? `<td class="r">${r.sheetId ? `<button class="linkbtn tb-train" data-on="${r.training ? '1' : '0'}" style="font-size:11px;white-space:nowrap">${r.training ? 'unmark' : 'log as training'}</button>` : ''}</td>` : ''}
          </tr>`).join('') || `<tr><td colspan="${isReviewer ? 5 : 4}" class="g-note">No live timesheet entries yet.</td></tr>`}
        </tbody>
      </table></div>
      ${trainMins ? `<p class="g-note" style="margin-top:12px">${hoursHM(trainMins / 60)} logged as training (included in the totals above).</p>` : ''}
      ${Number(project.histMins) ? `<p class="g-note" style="margin-top:6px">Includes ${hoursHM(Number(project.histMins) / 60)} of pre-launch time imported from the old system — counted in the per-person totals, but individual weeks aren't itemised.</p>` : ''}`;

    // Two levels deep, so the header's back link points at the RECORD, not at
    // the Projects list; re-rendering the record puts its own back link back.
    headerBack(`Back to ${project.number || 'project'}`,
      () => renderProjectDetail(mount, projectId, session, onBack, navOpts));
    window.scrollTo(0, 0);
    mount.querySelector('#tb-back')?.addEventListener('click', () => renderProjectDetail(mount, projectId, session, onBack, navOpts));
    mount.querySelector('#tb-back-list')?.addEventListener('click', () => onBack && onBack());

    mount.querySelectorAll('.tb-train').forEach((b) => b.addEventListener('click', async () => {
      const tr = b.closest('tr'); const on = b.dataset.on !== '1';
      b.disabled = true; b.textContent = '…';
      try {
        await setTimesheetRowTraining(tr.dataset.sheet, Number(tr.dataset.row), on);
        const entry = timeEntries.find((e) => e.sheetId === tr.dataset.sheet && e.rowIndex === Number(tr.dataset.row));
        if (entry) entry.training = on;
        b.dataset.on = on ? '1' : '0'; b.textContent = on ? 'unmark' : 'log as training'; b.disabled = false;
        tr.querySelector('.tbk-train-pill').innerHTML = on ? pill('training', 'hold') : '';
      } catch (e) { b.disabled = false; b.textContent = 'failed'; }
    }));
  };

  const timeBtn = mount.querySelector('#open-time');
  if (timeBtn) timeBtn.addEventListener('click', showTimeBreakdown);

  const dueBtn = mount.querySelector('#edit-due');
  // Callable from anywhere on the page — the deadline figure, "Set a deadline",
  // the "Agree a new date" chip. v4.19.4 (Karan 28/10/2026): the one value is
  // changed IN PLACE (7a/6b's "always preferred" inline edit, the follow-up the
  // old panel left as a comment), in a small date popover at whatever opened
  // it. Same write as before (project.due), now with its change-list line.
  const fmtDay = (v) => { const d = dayValue(v); return d ? d.split('-').reverse().join('/') : null; };
  async function saveDue(next) {
    const from = dayValue(project.due);
    if ((next || '') === from) return;
    const at = new Date().toISOString();
    await updateProjectFields(projectId, { due: next || null, updatedBy: session.email, updatedAt: at });
    await addRecordChange({ record: 'projects/' + projectId, field: 'due', label: 'Deadline', from: fmtDay(from), to: fmtDay(next), by: session.email, byName: session.displayName, at, money: false })
      .catch((e) => console.warn('[deadline] change list not written —', e && e.message));
    project.due = next || null;
  }
  const openDueEdit = (anchor) => {
    const at = (anchor && anchor.nodeType === 1 && anchor.isConnected) ? anchor
      : (mount.querySelector('#due-edit') || mount.querySelector('#kpi-due') || mount.querySelector('.pd4-figs .fig'));
    if (!at) return;
    openDateEditor(at, {
      value: project.due || '', heading: 'Deadline', clearLabel: 'No deadline',
      onSave: async (next) => { await saveDue(next); await reRender(); refocus('#due-edit, #kpi-due'); },
    });
  };
  if (dueBtn) dueBtn.addEventListener('click', (ev) => openDueEdit(ev.currentTarget));
  mount.querySelector('#kpi-due')?.addEventListener('click', (ev) => openDueEdit(ev.currentTarget));
  mount.querySelector('#due-edit')?.addEventListener('click', (ev) => openDueEdit(ev.currentTarget));

  // Manual, forward-only status setter (Karan spec 09/07). Works today; the
  // automation engine writes the same field. Lead roles only.
  // Review-stale prompt (Karan 10/07): sitting at Review ≥2 days → ask if issued.
  const staleBtns = mount.querySelectorAll('#review-stale [data-rev]');
  staleBtns.forEach((b) => b.addEventListener('click', () => {
    if (b.dataset.rev === 'dismiss') { mount.querySelector('#review-stale').remove(); return; }
    setStatus(b.dataset.rev).catch(() => {});
  }));

  // Set status to ANY value (forward OR back — Karan 15/07 "I need to go back"),
  // updating just the pill in place rather than re-rendering the whole page.
  const setStatus = async (next) => {
    const prev = project.status;
    const sel = mount.querySelector('#status-sel');
    if (sel) sel.disabled = true;
    try {
      await updateProjectFields(projectId, {
        status: next, statusUpdatedAt: new Date().toISOString(),
        statusSource: `manual (${session.email})`, updatedBy: session.email, updatedAt: new Date().toISOString(),
      });
      project.status = next;
      if (sel) { sel.value = next; sel.className = 'status-select status-' + next.replace(/\s+/g, '-').toLowerCase(); }
      const stale = mount.querySelector('#review-stale'); if (stale) stale.remove();
    } catch (e) {
      if (sel) sel.value = prev; // revert the dropdown to the real value
      alert('Couldn’t update status: ' + (e.message || e));
      throw e;
    } finally { if (sel) sel.disabled = false; }
  };

  // The status dropdown → set it directly (no page refresh).
  const statusSel = mount.querySelector('#status-sel');
  if (statusSel) statusSel.addEventListener('change', () => setStatus(statusSel.value).catch(() => {}));

  // ── Edit-patterns (pilot only) ──────────────────────────────────────────────
  // 6e status enum pill · 6g Close/On-hold endings · 6d Edit-details slide-over ·
  // 6h change list. All additive + gated on editPatterns; nothing here wires up for
  // a non-pilot viewer (the hosts aren't in the DOM). Function declarations are
  // hoisted, so this call block can sit above their definitions.
  if (editPatterns) {
    initStatusPill();
    renderProjectChangeList();
    mount.querySelector('#edit-project-panel')?.addEventListener('click', openEditPanel);
  }

  // Full-record re-render — the established pattern here after any write.
  function reRender() { return renderProjectDetail(mount, projectId, session, onBack, navOpts); }
  // After an in-place edit the page redraws; focus goes back to the value that
  // was changed (keyboard users keep their place).
  function refocus(sel) { const el = mount.querySelector(sel); if (el && el.focus) el.focus(); }

  // The time figure was rendered as "still arriving" because its read had not
  // come back inside the deadline. When it does, put the real number up — but
  // only if this record is still the screen and nothing would be disturbed by
  // redrawing it. The second render finds the answer memoised, so it is cheap.
  if (timePending && sheetsP) {
    sheetsP.then(() => {
      if (!mount.isConnected) return;
      if (document.querySelector('.dr-viewer, .askbar-panel, .report-issue-overlay, [class*="overlay"]:not([hidden]), dialog[open], .icd')) return;
      const a = document.activeElement;
      if (a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.isContentEditable)) return;
      renderProjectDetail(mount, projectId, session, onBack, { ...navOpts, quiet: true });
    }).catch(() => {});
  }

  // 6e — the status enum pill replaces the plain status control. Live work states
  // plus the two endings (On hold / Close). The pill does the affordance; the write
  // + change-list line happen in onChange (normal moves) or the 6g ending confirm.
  function initStatusPill() {
    const host = mount.querySelector('#status-pill');
    if (!host) return;
    const STATUS_OPTS = [
      { value: 'In Progress', label: 'In Progress', consequence: 'Active work, counts in the pipeline' },
      { value: 'Review', label: 'Review', consequence: 'Checking before issue' },
      { value: 'Ready to Issue', label: 'Ready to Issue', consequence: 'Ready to go out' },
      { value: 'Part Issued', label: 'Part Issued', consequence: 'Some deliverables out' },
      { value: 'Issued', label: 'Issued', consequence: 'All deliverables issued' },
      { value: 'Complete', label: 'Complete', consequence: 'Finished and closed out' },
      { value: 'On hold', label: 'On hold…', consequence: 'Time logged is flagged for review', ending: true },
      { value: 'Void', label: 'Void — never started…', consequence: 'For numbers where nothing ever happened', ending: true },
    ];
    renderPill(host, {
      value: project.status,
      disabled: !canStatus,
      gateRole: canStatus ? undefined : 'Owner · Director',
      options: STATUS_OPTS,
      onChange: async (v, reason) => {
        // On hold / Close alter others' work (held time is flagged, the project drops
        // out of the live list) → route to the full 6g ending confirm, which states
        // what stays / what changes and takes the reason. The WRITE happens in the
        // confirm's onConfirm, never here. `reason` carries any text the pill already
        // collected, so it prefills the confirm rather than being asked twice.
        if (v === 'On hold') { openHoldConfirm(reason); return; }
        if (v === 'Void') { openVoidConfirm(reason); return; }
        // Normal forward/back transition — commit immediately + one change-list line.
        const from = project.status; const at = new Date().toISOString();
        await updateProjectFields(projectId, { status: v, statusUpdatedAt: at, statusSource: `manual (${session.email})`, updatedBy: session.email, updatedAt: at });
        await addRecordChange({ record: 'projects/' + projectId, field: 'status', label: 'Status', from, to: v, by: session.email, byName: session.displayName, at, money: false });
        project.status = v;
        reRender();
      },
    });
  }

  // 6g — Put on hold. The app's real held status is 'On Hold' (capital H — the value
  // the sync + keystone agent read; it's not in ACTIVE_STATUSES, so the project drops
  // out of the live pipeline). The change list records the status transition + reason.
  function openHoldConfirm(prefillReason) {
    const from = project.status;
    openEndingConfirm({
      eyebrow: 'Put on hold',
      record: `${project.number || projectId} — ${project.title || clientName}`,
      whatStays: ['All time already logged, at its rates', 'Every document and instruction filed', 'The client and fee, unchanged'],
      whatChanges: 'Drops out of the live pipeline. Time logged against a held project is flagged for review. Can be resumed.',
      field: { label: 'Reason — goes on the record', type: 'text', placeholder: 'e.g. awaiting revised loadings', value: prefillReason || '' },
      confirmLabel: 'Put on hold', reversibleNote: 'Reversible',
      onConfirm: async (reason) => {
        const at = new Date().toISOString();
        await updateProjectFields(projectId, { status: 'On Hold', heldReason: reason || null, statusBeforeHold: from, statusUpdatedAt: at, statusSource: `manual (${session.email})`, updatedBy: session.email, updatedAt: at });
        await addRecordChange({ record: 'projects/' + projectId, field: 'status', label: 'Status', from, to: 'On Hold', reason: reason || null, by: session.email, byName: session.displayName, at, money: false });
        project.status = 'On Hold';
        reRender();
      },
    });
  }

  // Void (Karan 11/08): "Close project is not needed — Complete/Issued deals
  // with that. Void/dead should be available manually too." For numbers where
  // the work never happened — the number is never reused, the record stays.
  function openVoidConfirm(prefillReason) {
    const from = project.status;
    openEndingConfirm({
      eyebrow: 'Void — never started',
      record: `${project.number || projectId} — ${project.title || clientName}`,
      whatStays: ['The project number — numbers are never reused', 'The register entry and its history', 'A Lost enquiry holding the client, scope and quote — reopen it if the work comes back'],
      whatChanges: 'Marks a project that never actually happened. It leaves the live list for good. If work WAS done, use Complete instead — Void is not for finished work.',
      field: { label: 'Why it never started', type: 'text', placeholder: 'e.g. enquiry withdrawn before instruction', value: prefillReason || '' },
      confirmLabel: 'Mark as Void', reversibleNote: 'Reversible by setting a status again',
      onConfirm: async (reason) => {
        const at = new Date().toISOString();
        await updateProjectFields(projectId, { status: 'Void', statusNote: reason || null, statusUpdatedAt: at, statusSource: `manual (${session.email})`, updatedBy: session.email, updatedAt: at });
        await addRecordChange({ record: 'projects/' + projectId, field: 'status', label: 'Status', from, to: 'Void', reason: reason || null, by: session.email, byName: session.displayName, at, money: false });
        project.status = 'Void';
        // Void cascade (Karan 17/08): flip/create the Lost enquiry so the dead
        // job stays revivable from the pipeline. Non-fatal — the Void has landed.
        try { await cascadeVoidToEnquiry(project, reason, session); }
        catch (e) { console.warn('[void] enquiry cascade failed:', e); }
        reRender();
      },
    });
  }

  // CHANGE CLIENT (v4.6.2, Karan 26/10/2026 on 7786: "what do I do if I need to
  // change the client of a project??"). Both Edit details surfaces show the
  // client with "Change…": a searchable picker (or a new client), then a
  // confirmation saying what stays — invoices already raised keep the client
  // they went to — and the server writes the change (changeProjectClient).
  // Same roles as editing the details (the rules' reviewer()).
  function chooseClient(onChosen) {
    Promise.all([allClients().catch(() => []), allProjects().catch(() => [])]).then(([cs, ps]) => {
      openClientPicker({ number: project.number || projectId, currentId: currentClientId, currentName: shownClient, clients: cs || [], projects: ps || [], onPick: onChosen });
    });
  }
  // The Drive folder (v4.7.3, Karan 26/10/2026: "should it rename the drive
  // folder too?"): the folder linked on the project, or the one the page found
  // by its number. Its name is read as Drive has it now (read token, 4 s at
  // most); unreadable → the confirmation says what it said before.
  async function projectFolderNow() {
    const id = project.folderId || (job && !job.error && job.folderId) || null;
    if (!id) return { id: null, name: null };
    if (!hasToken()) return { id, name: null };
    const f = await Promise.race([getDriveFile(id).catch(() => null), new Promise((r) => setTimeout(() => r(null), 4000))]);
    return { id, name: (f && !f.trashed && f.name) || null };
  }
  async function confirmClientChange(pick) {
    const number = project.number || projectId;
    const toName = pick.id ? pick.name : properName(pick.newName);
    const folderNow = await projectFolderNow();
    const folder = folderNow.name
      ? { name: folderNow.name, ...folderRename({ name: folderNow.name, number, fromName: [shownClient, project.clientName, project.client], toName }) }
      : null;
    const copy = confirmCopy({
      number, fromName: shownClient, toName, isNew: !pick.id,
      invoicesRaised: raisedInvoices(invoices), hasFolder: !!(folderNow.id || project.folderUrl), folder,
    });
    return new Promise((resolve) => {
      openClientChangeConfirm({
        copy,
        onConfirm: async ({ rename } = {}) => {
          const want = rename && copy.rename ? copy.rename : null;
          // Asked for HERE, in the click, so Google's consent can open; awaited
          // only once the client change has gone through.
          const tokP = want ? driveWriteToken() : null;
          if (tokP) tokP.catch(() => {});
          const out = await applyClientChange({
            change: () => changeProjectClient(projectId, pick),
            rename: want,
            renameFolder: (to) => renameDriveFile(folderNow.id, to, tokP),
            recordChange: (ln) => addRecordChange({ record: 'projects/' + projectId, ...ln, by: session.email, byName: session.displayName, at: new Date().toISOString(), money: false }),
          });
          toastV4(out.toast, out.failed ? 9000 : 3200);
          resolve(true);
        },
        onCancel: () => resolve(false),
      });
    });
  }
  // The client row's behaviour, for either surface: [data-pd-client] shows the
  // name, [data-pd-client-change] opens the picker, [data-pd-client-box] glows
  // while a change is waiting for Save, [data-pd-client-note] says what it was.
  function wireClientField(root, onStage) {
    const nameEl = root.querySelector('[data-pd-client]');
    const box = root.querySelector('[data-pd-client-box]');
    const note = root.querySelector('[data-pd-client-note]');
    const stage = (pick) => {
      onStage(pick);
      nameEl.textContent = pick ? (pick.id ? pick.name : `${properName(pick.newName)} (new client)`) : (shownClient || 'No client');
      box.style.borderColor = pick ? 'var(--link)' : 'var(--hair-strong)';
      box.style.boxShadow = pick ? '0 0 0 3px var(--halo)' : 'none';
      note.hidden = !pick;
      if (pick) {
        note.innerHTML = `Was ${escapeHtml(shownClient || 'no client')}. You confirm the change when you save. <button type="button" class="pd4-link" data-pd-client-undo style="font-size:12px">Undo</button>`;
        note.querySelector('[data-pd-client-undo]').addEventListener('click', () => stage(null));
      }
    };
    root.querySelector('[data-pd-client-change]').addEventListener('click', () => chooseClient(stage));
  }

  // 6d — the Edit-details slide-over (fixed right, 520px). Fields: Title, Client
  // reference, Site address, Deadline (the planning reference moved to References,
  // 02/11/2026, where a project can hold more than one). Changed fields glow
  // (blue border + halo); Save writes ONE updateProjectFields for the changed keys
  // and ONE change-list line per changed field. Fee / status / project number /
  // revision are deliberately NOT here (status is the pill; fee is its own flow).
  function openEditPanel() {
    const number = project.number || projectId;
    const title0 = project.title || project.description || '';
    const site0 = (project.siteAddress && project.siteAddress.line1) || project.site || '';
    const lastEdited = project.updatedAt ? new Date(project.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
    const LABEL = 'font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--ink-3)';
    const BOX = 'display:flex;align-items:center;gap:8px;border:1px solid var(--hair-strong);border-radius:8px;padding:0 13px;background:var(--surface)';
    const INPUT = 'flex:1;min-width:0;border:0;background:transparent;padding:10px 0;font-size:13.5px;color:inherit;font-family:inherit';
    const field = (id, label, val, { full = false, type = 'text' } = {}) => `
      <div style="display:flex;flex-direction:column;gap:7px${full ? ';grid-column:1 / -1' : ''}">
        <span style="${LABEL}">${escapeHtml(label)}</span>
        <div class="pe-box" style="${BOX}"><input id="${id}" type="${type}" autocomplete="off" value="${escapeHtml(val)}" style="${INPUT}"></div>
      </div>`;

    const wrap = document.createElement('div');
    const scrim = document.createElement('div');
    scrim.setAttribute('style', 'position:fixed;inset:0;background:rgba(var(--scrim-rgb), 0.42);z-index:600;opacity:0;transition:opacity 200ms var(--ease-out)');
    const panel = document.createElement('div');
    panel.setAttribute('style', 'position:fixed;top:0;right:0;bottom:0;width:520px;max-width:calc(100 * var(--vw));background:var(--surface);border-left:1px solid var(--hair);box-shadow:-18px 0 48px rgba(12,17,57,0.18);display:flex;flex-direction:column;z-index:601;transform:translateX(100%);transition:transform 200ms var(--ease-out)');
    panel.innerHTML = `
      <div style="padding:26px 30px 20px;border-bottom:1px solid var(--hair);display:flex;align-items:flex-start;gap:14px">
        <div style="flex:1;display:flex;flex-direction:column;gap:5px">
          <span style="${LABEL};color:var(--brand-orange)">Edit project</span>
          <span style="font-size:24px;font-weight:500;letter-spacing:-0.025em">${escapeHtml(number + ' ' + (title0 || clientName))}</span>
          ${lastEdited ? `<span style="font-size:12.5px;color:var(--ink-3)">Last edited ${escapeHtml(lastEdited)}${project.updatedBy ? ' · ' + escapeHtml(project.updatedBy) : ''}</span>` : ''}
        </div>
        <span id="pe-close" style="font-size:19px;color:var(--ink-3);line-height:1;cursor:pointer">&#10005;</span>
      </div>
      <div style="flex:1;padding:24px 30px;display:flex;flex-direction:column;gap:22px;overflow-y:auto">
        ${field('pe-title', 'Title', title0, { full: true })}
        ${canChangeClient ? `<div id="pe-client-row" style="display:flex;flex-direction:column;gap:7px">
          <span style="${LABEL}">Client</span>
          <div class="pe-box" data-pd-client-box style="${BOX}">
            <span data-pd-client style="flex:1;min-width:0;padding:10px 0;font-size:13.5px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escapeHtml(shownClient || 'No client')}</span>
            <button type="button" class="pd4-link" data-pd-client-change style="font-size:13px">Change…</button>
          </div>
          <span data-pd-client-note style="font-size:12px;color:var(--ink-3)" hidden></span>
        </div>` : ''}
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          ${field('pe-clientref', 'Client reference', project.clientReference || '')}
          ${field('pe-target', 'Deadline', project.due ? String(project.due).slice(0, 10) : '', { type: 'date' })}
        </div>
        ${field('pe-site', 'Site address', site0, { full: true })}
        <span style="font-size:12px;color:var(--ink-3)">Planning, building control and client PO references are under References on the project page.</span>
      </div>
      <div id="pe-error" style="display:none;padding:0 30px 8px;color:var(--status-error);font-size:12.5px"></div>
      <div style="padding:18px 30px 22px;border-top:1px solid var(--hair);display:flex;align-items:center;gap:12px">
        <span id="pe-save" style="height:40px;display:inline-flex;align-items:center;padding:0 22px;border-radius:8px;background:var(--brand-blue);color:var(--brand-white);font-size:13.5px;font-weight:500;cursor:pointer">Save changes</span>
        <span id="pe-cancel" style="height:40px;display:inline-flex;align-items:center;padding:0 18px;border-radius:8px;font-size:13.5px;color:var(--ink-2);cursor:pointer">Cancel</span>
        <span style="flex:1"></span>
        <span id="pe-count" style="font-size:12px;color:var(--ink-3)">0 changes</span>
      </div>`;
    wrap.appendChild(scrim); wrap.appendChild(panel); document.body.appendChild(wrap);
    requestAnimationFrame(() => { scrim.style.opacity = '1'; panel.style.transform = 'translateX(0)'; });

    let closed = false;
    const close = () => { if (closed) return; closed = true; document.removeEventListener('keydown', onKey); scrim.style.opacity = '0'; panel.style.transform = 'translateX(100%)'; setTimeout(() => wrap.remove(), 200); };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); close(); } };
    document.addEventListener('keydown', onKey);
    scrim.addEventListener('click', close);
    panel.querySelector('#pe-close').addEventListener('click', close);
    panel.querySelector('#pe-cancel').addEventListener('click', close);

    // Field defs — DOM id, change-list label, Firestore key, original value. Client
    // reference + planning reference are their own text fields (NOT the clientRef
    // link path). Title moves with description; site with siteAddress.line1.
    const defs = [
      { id: 'pe-title', label: 'Title', key: 'title', orig: title0 },
      { id: 'pe-clientref', label: 'Client reference', key: 'clientReference', orig: project.clientReference || '' },
      { id: 'pe-site', label: 'Site address', key: 'site', orig: site0 },
      { id: 'pe-target', label: 'Deadline', key: 'due', orig: project.due ? String(project.due).slice(0, 10) : '' },
    ];
    const elOf = (d) => panel.querySelector('#' + d.id);
    const norm = (s) => String(s == null ? '' : s).trim();
    const isChanged = (d) => { const el = elOf(d); return !!el && norm(el.value) !== norm(d.orig); };
    const saveBtn = panel.querySelector('#pe-save');
    const countEl = panel.querySelector('#pe-count');
    const errBox = panel.querySelector('#pe-error');
    const setSave = (on) => { saveBtn.style.opacity = on ? '1' : '0.45'; saveBtn.style.pointerEvents = on ? 'auto' : 'none'; saveBtn.style.cursor = on ? 'pointer' : 'default'; };
    let pendingClient = null;   // v4.6.2: a client picked, waiting for Save
    const recompute = () => {
      let count = pendingClient ? 1 : 0;
      for (const d of defs) {
        const el = elOf(d); if (!el) continue;
        const ch = isChanged(d); if (ch) count++;
        const box = el.closest('.pe-box');
        if (box) { box.style.borderColor = ch ? 'var(--brand-blue)' : 'var(--hair-strong)'; box.style.boxShadow = ch ? '0 0 0 3px var(--halo)' : 'none'; }
      }
      countEl.textContent = count + ' change' + (count === 1 ? '' : 's');
      setSave(count > 0);
    };
    defs.forEach((d) => { const el = elOf(d); if (el) { el.addEventListener('input', recompute); el.addEventListener('change', recompute); } });
    const clientRow = panel.querySelector('#pe-client-row');
    if (clientRow) wireClientField(clientRow, (pick) => { pendingClient = pick; recompute(); });
    setSave(false);

    saveBtn.addEventListener('click', async () => {
      const changed = defs.filter(isChanged);
      if (!changed.length && !pendingClient) return;
      setSave(false); saveBtn.textContent = 'Saving…'; errBox.style.display = 'none';
      // A client change is confirmed first and written server-side (it is the one
      // edit here that touches more than this record's own fields).
      if (pendingClient) {
        if (!(await confirmClientChange(pendingClient))) { saveBtn.textContent = 'Save changes'; setSave(true); return; }
        pendingClient = null;
        if (!changed.length) { close(); reRender(); return; }
      }
      const at = new Date().toISOString();
      const fields = { manualOverride: true, updatedBy: session.email, updatedAt: at };
      const lines = [];
      for (const d of changed) {
        const to = norm(elOf(d).value);
        if (d.key === 'site') { fields.site = to || null; fields.siteAddress = to ? { line1: to } : null; }
        else if (d.key === 'title') { fields.title = to || null; fields.description = to || null; } // title + description move together (matches the existing edit + the summary derivations)
        else { fields[d.key] = to || null; }
        lines.push({ field: d.key, label: d.label, from: d.orig || null, to: to || null });
      }
      try {
        await updateProjectFields(projectId, fields);
        for (const ln of lines) await addRecordChange({ record: 'projects/' + projectId, field: ln.field, label: ln.label, from: ln.from, to: ln.to, by: session.email, byName: session.displayName, at, money: false });
        close();
        reRender();
      } catch (e) { saveBtn.textContent = 'Save changes'; setSave(true); errBox.textContent = 'Couldn’t save — ' + (e && e.message || e); errBox.style.display = 'block'; }
    });
  }

  // 6h — "Changes to this record" at the foot of the record. Same treatment as the
  // person record (team.js): node colours (recent = blue+halo, reason-carrying =
  // orange, earlier = navy), Field from→to · person · date, 6 lines + Show all.
  function renderProjectChangeList() {
    const box = mount.querySelector('#pd-changes');
    if (!box) return;
    const fmtChangeDate = (iso) => {
      if (!iso) return '';
      const d = new Date(iso); if (isNaN(d.getTime())) return '';
      const now = new Date();
      if (d.toDateString() === now.toDateString()) return 'today ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
      if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
      return String(d.getFullYear());
    };
    const valSpan = (v, isNew) => {
      const txt = (v == null || v === '') ? '—' : String(v);
      return `<span style="${isNew ? 'font-weight:500;' : 'color:var(--ink-2);'}">${escapeHtml(txt)}</span>`;
    };
    const line = (c, recent, last) => {
      const nodeColor = recent ? 'var(--brand-blue)' : (c.reason ? 'var(--brand-orange)' : 'var(--ink)');
      const halo = recent ? 'box-shadow:0 0 0 3px var(--halo);' : '';
      const bodyHtml = c.baseline
        ? 'Record created'
        : `${escapeHtml(c.label || c.field || 'Field')} ${valSpan(c.from, false)} &#8594; ${valSpan(c.to, true)}${c.reason ? ` &#8212; <span style="color:var(--ink-2)">${escapeHtml(c.reason)}</span>` : ''}`;
      return `<div style="display:flex;gap:16px;padding:13px 0;${last ? '' : 'border-bottom:1px solid var(--hair-soft);'}">
        <span style="width:11px;flex:none;display:flex;justify-content:center;padding-top:5px"><span style="width:9px;height:9px;border-radius:50%;background:${nodeColor};${halo}"></span></span>
        <span style="flex:1;font-size:13.5px;line-height:1.5">${bodyHtml}</span>
        <span style="font-size:12.5px;color:var(--ink-3);flex:none">${escapeHtml(c.byName || c.by || '—')}</span>
        <span style="font-family:var(--font-technical);font-size:12px;color:var(--ink-3);flex:none;width:88px;text-align:right">${escapeHtml(fmtChangeDate(c.at))}</span>
      </div>`;
    };
    listRecordChanges('projects/' + projectId).then((changes) => {
      const all = (changes || []).slice(); // newest first (db.js sorts)
      const baseD = asDate(project.createdAt); // baseline only from a real timestamp
      if (baseD && !isNaN(baseD.getTime())) all.push({ baseline: true, label: 'Record created', at: baseD.toISOString(), byName: '' });
      if (!all.length) { box.innerHTML = '<p class="g-note" style="margin:0">No changes recorded yet.</p>'; return; }
      let expanded = false;
      const paint = () => {
        const shown = expanded ? all : all.slice(0, 6);
        const rows = shown.map((c, i) => line(c, i === 0 && !c.baseline, i === shown.length - 1)).join('');
        const more = (!expanded && all.length > 6)
          ? `<span id="pd-changes-more" style="display:inline-block;margin-top:14px;font-size:12.5px;font-weight:500;color:var(--link);cursor:pointer">Show all ${all.length}</span>` : '';
        box.innerHTML = `<div style="display:flex;flex-direction:column">${rows}</div>${more}`;
        box.querySelector('#pd-changes-more')?.addEventListener('click', () => { expanded = true; paint(); });
      };
      paint();
    }).catch(() => { box.innerHTML = '<p class="g-note" style="margin:0">Couldn’t load changes.</p>'; });
  }

  // One-click confirm / dismiss for automation suggestions (Won, Issued).
  const callSuggest = async (id, action) => {
    const fns = getFunctions(app, 'europe-west2');
    await httpsCallable(fns, 'confirmStatusSuggestion')({ id, action });
    renderProjectDetail(mount, projectId, session, onBack, navOpts);
  };
  mount.querySelectorAll('[data-confirm]').forEach((b) => b.addEventListener('click', () => {
    b.disabled = true; b.textContent = 'Confirming…';
    callSuggest(b.dataset.confirm, 'confirm').catch(() => { b.disabled = false; b.textContent = 'Confirm'; });
  }));
  mount.querySelectorAll('[data-dismiss]').forEach((b) => b.addEventListener('click', () =>
    callSuggest(b.dataset.dismiss, 'dismiss').catch(() => {})));

  let siteCtl = null; // the Site constraints block's controller, once mounted (declared before every use)
  const mapEl = mount.querySelector('#open-map');
  // Site location overlay = the site-checks pin drop (Pin | Layers), 15/10/2026.
  // The snapshot is fetched by the overlay when not passed; the Site
  // constraints block can pass its own and an onConfirmed that repaints.
  // Through the block's own opener once it is mounted, so a confirmed pin repaints it.
  if (mapEl) mapEl.addEventListener('click', () => {
    if (siteCtl) { siteCtl.openPin(); return; }
    import('./site-pin.js').then(({ openSitePin }) => openSitePin({ projectId, project, onConfirmed: () => {} }))
      .catch((e) => console.error('[site-pin] could not open', e));
  });

  const addInstrBtn = mount.querySelector('#add-instr');
  if (addInstrBtn) addInstrBtn.addEventListener('click', () => startAddInstruction());
  // ADD INSTRUCTION — REDESIGN (Karan 26/10/2026). A saved draft reopens where it
  // was left; a won/open enquiry for this client opens the small modal on "A won
  // enquiry" (page 4); otherwise the full draft opens in the table (page 3).
  // Numbering, the money and the enquiry link happen on the server
  // (createInstruction), never here.
  const instrEmailsP = () => (project.number
    ? projectEmails(project.number, 25, enqData && enqData.number).then((rows) => rows.map(normArchiver)).catch(() => [])
    : Promise.resolve([]));
  const draftCommon = () => ({
    host: mount.querySelector('#instr-list'), project: { ...project, id: projectId, clientName }, projectId, session,
    seq: Math.max(0, ...instructions.map((i) => Number(i.seq) || 0)) + 1,
    users: userOptions, defaultLead: tailRef(project.lead) || '',
    emailsP: instrEmailsP(), openEmail: (en) => openEmail(en),
    onDone: () => renderProjectDetail(mount, projectId, session, onBack, navOpts),
  });
  async function startAddInstruction() {
    const ov = mount.querySelector('.pd-tab[data-tab="overview"]');
    if (ov && !ov.classList.contains('active')) ov.click();
    const host = mount.querySelector('#instr-list');
    if (!host) return;
    if (host.querySelector('.icd')) { host.querySelector('.icd').scrollIntoView({ block: 'nearest' }); return; }
    const [cat, draft] = await Promise.all([loadCatalogue().catch(() => ({ enquiries: [] })), getInstructionDraft(projectId).catch(() => null)]);
    const candidates = candidateEnquiries(cat.enquiries || [], project);
    const common = { ...draftCommon(), candidates };
    if (draft) { openInstructionDraft({ ...common, resume: draft }); return; }
    if (candidates.length) {
      openAddInstructionQuick({ ...common, onOpenDraft: (prefill) => openInstructionDraft({ ...common, resume: null, prefill }) });
      return;
    }
    openInstructionDraft({ ...common, resume: null });
  }
  // "Add original scope" (the alert's fix) opens the same panel as instruction 1,
  // described "Original scope", with the project's fee. (Every OTHER add goes
  // through the redesign above; instruction 1 is the sync's, so this legacy
  // panel stays only for that alert — a follow-up moves it onto the draft.)
  function openAddInstruction(prefill = {}) {
    const nextSeq = Math.max(0, ...instructions.map((i) => Number(i.seq) || 0)) + 1;
    // Add instruction MOVED from modal to panel (Dialogs & Forms §1, build item 9):
    // it adds to a project that exists and the instruction stack behind it is the
    // context. The calculator is bespoke, so we drive the panel body ourselves.
    // DIALOGS&FORMS Q: spec §9.4 — moving this to a panel is a judgement. If it
    // turns out instructions are usually written without the stack in view, this
    // should revert to a modal. Watch it in the pilot before deciding.
    const calcLines = [];
    let cDesc, cHours, cFee, cDue, cStatus, cItem, cQty, cLineHrs, linesEl;
    const drawLines = () => {
      linesEl.innerHTML = calcLines.length
        ? calcLines.map((l) => `${escapeHtml(l.item)} ×${l.qty} · ${l.hours}h`).join('<br>')
        : 'No lines yet.';
      const hrs = calcLines.reduce((t, l) => t + l.hours * l.qty, 0);
      if (hrs) {
        cHours.set(hrs);
        if (!cFee.get()) cFee.el.querySelector('input').placeholder = String(Math.round(hrs * (Number(orgSetting('chargeOutRate', 60)) || 60)));
      }
    };
    openPanel({
      eyebrow: `Instruction #${nextSeq}`,
      title: 'Add instruction',
      subtitle: `${project.number} · ${clientName}`,
      render(body, { field }) {
        cDesc = field({ key: 'desc', label: 'Description', type: 'text', span: true, required: true, placeholder: 'What has been instructed?', value: prefill.description || '' });
        cHours = field({ key: 'hours', label: 'Budget hours', type: 'number', placeholder: '0', value: prefill.budgetHours || '' });
        cFee = field({ key: 'fee', label: 'Fee', type: 'money', affix: '£', value: prefill.fee || '' });
        cDue = field({ key: 'due', label: 'Due date', type: 'date', optional: true });
        cStatus = field({ key: 'status', label: 'Status', type: 'select', value: prefill.status || 'Not Started', options: ['Not Started', 'In Progress', 'Review', 'Ready to Issue', 'Part Issued', 'Issued'].map((s) => ({ value: s, label: s })) });
        const grid = document.createElement('div');
        grid.className = 'tf-grid';
        [cDesc, cHours, cFee, cDue, cStatus].forEach((c) => grid.appendChild(c.el));
        body.appendChild(grid);

        // Calculator — a small line adder that fills Budget hours. Kept as bespoke
        // DOM on the shell's tokens; each add-line control is a shell field.
        const calc = document.createElement('div');
        calc.style.marginTop = '24px';
        calc.innerHTML = '<div class="tf-section-eyebrow">Calculator</div>';
        linesEl = document.createElement('div');
        linesEl.style.cssText = 'font-size:12.5px;color:var(--ink-3);margin-bottom:10px';
        linesEl.textContent = 'No lines yet.';
        calc.appendChild(linesEl);
        cItem = field({ key: '_item', label: 'Item', type: 'text' });
        cQty = field({ key: '_qty', label: 'Qty', type: 'number2', value: 1 });
        cLineHrs = field({ key: '_lhrs', label: 'Hours', type: 'number', placeholder: 'hrs' });
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;gap:10px;align-items:flex-end';
        cItem.el.style.flex = '2';
        const addBtn = document.createElement('button');
        addBtn.type = 'button'; addBtn.className = 'tf-btn tf-btn-secondary'; addBtn.textContent = '+ Add'; addBtn.style.flex = 'none';
        addBtn.addEventListener('click', () => {
          const item = cItem.get(); if (!item) return;
          calcLines.push({ item, qty: Number(cQty.get()) || 1, hours: Number(cLineHrs.get()) || 0 });
          cItem.set(''); cLineHrs.set(''); cQty.set(1);
          drawLines();
        });
        row.appendChild(cItem.el); row.appendChild(cQty.el); row.appendChild(cLineHrs.el); row.appendChild(addBtn);
        calc.appendChild(row);
        body.appendChild(calc);
      },
      primary: {
        label: 'Add instruction',
        onSubmit: async () => {
          if (!cDesc.validate()) { cDesc.focus(); throw new Error('Description is required.'); }
          resumeIfHeld(projectId, session.email, session.displayName, 'an instruction was added').catch(() => {});
          await addInstruction(projectId, nextSeq, {
            seq: nextSeq, description: cDesc.get(),
            budgetHours: Number(cHours.get()) || null,
            fee: Number(cFee.get()) || Number(cFee.el.querySelector('input').placeholder) || null,
            due: cDue.get() || null,
            status: cStatus.get(),
            instructedAt: new Date().toISOString(),
            createdBy: session.email, calculatorItems: calcLines, invoiceRefs: [],
          });
          renderProjectDetail(mount, projectId, session, onBack, navOpts);
        },
      },
    });
  }

  // Edit an existing instruction (any, instruction 1 included since v4.20.0 — the V1 sync is retired) — golden thread
  // (Karan 23/07). Edits a record that EXISTS, so it is a PANEL, not a modal
  // (Dialogs & Forms §1 / item-9 sweep: a modal that edits an existing record is
  // a bug in this system).
  // Edit instruction uses the SAME calculator (decision 2, 26/10/2026): its row
  // opens as a draft inline, with the status (forward only) and the lead.
  page.addEventListener('click', (ev) => {
    const b = ev.target && ev.target.closest ? ev.target.closest('.instr-edit') : null;
    if (!b || !page.contains(b)) return;
    ev.stopPropagation();
    const instr = instructions.find((i) => String(i.seq) === b.dataset.seq);
    if (!instr) return;
    openInstructionDraft({ ...draftCommon(), mode: 'edit', seq: Number(instr.seq), instr, row: b, candidates: [],
      onClose: () => {} });
  });

  // Move an invoice to a different instruction (golden thread, Finance only).
  // DIALOGS&FORMS Q: this reassigns one value on an existing invoice via a modal
  // row-picker, which the item-9 sweep flags (a modal touching an existing
  // record). It is a single choice, not a multi-field edit, so the right home is
  // an inline select/enum-pill (6e) on the invoice row rather than the create/edit
  // shell — left as-is pending that rework; not converted here to avoid inventing.
  mount.querySelectorAll('.inv-move').forEach((b) => b.addEventListener('click', () => {
    const iv = invoices.find((x) => String(x.id) === b.dataset.id);
    if (!iv) return;
    const cur = Number(b.dataset.seq) || 1;
    const { content, close } = openOverlay({ title: `Move INV ${iv.number}`, subtitle: `${money(Number(iv.amount) || 0)} · pick the instruction it belongs to` });
    content.innerHTML = `<div style="padding:12px 16px 16px">${instructions.map((i) => `
      <div class="ct-row inv-pick" style="grid-template-columns:44px 1fr auto;padding:11px 12px;cursor:pointer" data-seq="${escapeHtml(String(i.seq))}">
        <span class="mono strong">#${escapeHtml(String(i.seq))}</span>
        <span>${escapeHtml(i.description || 'Instruction')}${i.status ? ` <span class="sec">· ${escapeHtml(i.status)}</span>` : ''}</span>
        <span>${Number(i.seq) === cur ? '<span class="sec">current</span>' : ''}</span>
      </div>`).join('')}<div class="form-error" id="iv-err" style="padding:0 4px"></div></div>`;
    content.querySelectorAll('.inv-pick').forEach((r) => r.addEventListener('click', async () => {
      const seq = Number(r.dataset.seq);
      if (seq === cur) { close(); return; }
      try {
        await setInvoiceInstruction(iv.id, seq, session);
        close();
        renderProjectDetail(mount, projectId, session, onBack, navOpts);
      } catch (e) { const el = content.querySelector('#iv-err'); if (el) el.textContent = e.message || String(e); }
    }));
  }));

  // (Activity is built from the project's own records — see the 07/10 block at the end.)

  // Feedback golden-thread (Karan 14/07): show this project's feedback/complaints
  // on the project page, linking the two together.
  if (project.number) {
    const fbMount = mount.querySelector('#pd-feedback');
    feedbackForProject(project.number).then((all) => {
      // Only confirmed feedback on the project page — AI suggestions wait in the
      // Feedback review queue until someone confirms them.
      const rows = all.filter((f) => !f.needsReview && f.status !== 'Dismissed');
      if (!rows.length) { fbMount.innerHTML = ''; return; }
      rows.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
      fbMount.innerHTML = `<div class="pd4-sec-h" style="margin-top:28px"><h2>Feedback</h2></div>
        <div class="pd-fb-list">${rows.map((f) => `
          <div class="pd-fb-row">
            <div class="pd-fb-top">${pill(f.type || 'Feedback', f.type === 'Complaint' ? 'error' : 'info')}
              ${pill(f.status || 'Open', f.status === 'Resolved' ? 'ok' : f.status === 'In Progress' ? 'info' : 'error')}
              <span class="pd-fb-from">${escapeHtml(f.source || '')}</span>
              <span class="pd-fb-date">${f.createdAt ? fmtDate(f.createdAt) : ''}</span></div>
            <div class="pd-fb-text">${escapeHtml(f.text || '')}</div>
          </div>`).join('')}</div>`;
    }).catch(() => { fbMount.innerHTML = ''; });
  }

  // Notes — part of the project record (handoff-ask-track 21b): one list shared
  // between notes written here and notes written from the Ask Track bar. Each
  // carries who, when, and the screen it was written from. Not an instruction:
  // a note cannot be billed, approved or issued.
  const notesMount = mount.querySelector('#pd-notes');
  if (notesMount && project.number) {
    const noteWhen = (v) => {
      const d = v && (v.toDate ? v.toDate() : new Date(v));
      if (!d || Number.isNaN(d.getTime())) return '';
      const two = (n) => String(n).padStart(2, '0');
      return `${two(d.getDate())} ${d.toLocaleDateString('en-GB', { month: 'short' })} ${two(d.getHours())}:${two(d.getMinutes())}`;
    };
    const drawNotes = async () => {
      const rows = (await notesForProject(project.number).catch(() => []))
        .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
      notesMount.innerHTML = `<div style="display:flex;flex-direction:column;gap:18px;margin-top:26px">
        <div style="display:flex;align-items:baseline;gap:12px">
          <span style="font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--link)">Notes</span>
          <div style="flex:1"></div>
          ${rows.length ? `<span class="g-mono" style="font-size:12px;color:var(--ink-3)">${String(rows.length).padStart(2, '0')}</span>` : ''}
          <button id="pd-note-add" style="font-size:12px;color:var(--link);background:none;border:0;cursor:pointer;padding:0">Add a note</button>
        </div>
        <div id="pd-note-form" hidden style="display:flex;flex-direction:column;gap:10px">
          <textarea id="pd-note-text" rows="2" placeholder="What do you want to remember?" style="width:100%;border:1px solid var(--hair);border-radius:10px;padding:10px 12px;font-family:var(--font-sans);font-size:13.5px;line-height:1.5;color:var(--ink);resize:vertical"></textarea>
          <div style="display:flex;gap:12px;align-items:center">
            <button id="pd-note-save" style="background:var(--brand-blue);color:var(--brand-white);border:0;border-radius:8px;padding:8px 16px;font-size:12.5px;font-weight:500;cursor:pointer">Save note</button>
            <button id="pd-note-cancel" style="background:none;border:0;font-size:12.5px;color:var(--ink-3);cursor:pointer;padding:0">Cancel</button>
          </div>
        </div>
        <div style="display:flex;flex-direction:column">
          ${rows.map((n, i) => `<div style="display:grid;grid-template-columns:132px 1fr;gap:22px;padding:16px 0;${i ? 'border-top:1px solid var(--hair);' : ''}">
            <span style="display:flex;flex-direction:column;gap:3px">
              <span style="font-size:12.5px;font-weight:500">${escapeHtml(n.userName || n.userEmail || '')}</span>
              <span class="g-mono" style="font-size:11px;color:var(--ink-3)">${escapeHtml(noteWhen(n.createdAt))}</span>
            </span>
            <span style="display:flex;flex-direction:column;gap:6px;min-width:0">
              <span style="font-size:14px;line-height:1.55;text-wrap:pretty">${escapeHtml(n.text || '')}</span>
              <span style="font-size:11.5px;color:var(--ink-3)">${escapeHtml([n.via === 'ask-track' ? `Added from ${n.addedFrom || 'Track'}, by Ask Track` : `Added from ${n.addedFrom || 'the project record'}`, n.instructionSeq ? `Instruction ${n.instructionSeq}` : null].filter(Boolean).join(' · '))}</span>
            </span>
          </div>`).join('') || '<span class="g-note">No notes yet — anyone on the project can add one, here or from the Ask Track bar.</span>'}
        </div>
      </div>`;
      const form = notesMount.querySelector('#pd-note-form');
      notesMount.querySelector('#pd-note-add').addEventListener('click', () => { form.hidden = false; notesMount.querySelector('#pd-note-text').focus(); });
      notesMount.querySelector('#pd-note-cancel').addEventListener('click', () => { form.hidden = true; });
      notesMount.querySelector('#pd-note-save').addEventListener('click', async () => {
        const text = notesMount.querySelector('#pd-note-text').value.trim();
        if (!text) return;
        await addNote({
          text, userName: myName, userEmail: session.email,
          projectNo: String(project.number), projectId: project.id,
          addedFrom: 'the project record', createdAt: new Date().toISOString(),
        }).catch(() => {});
        drawNotes();
      });
    };
    drawNotes();
  }

  // Project issues log (Karan 16/07): log an issue found on the project, click in
  // to read it and record what's going to be done. Reviewers can add/act.
  let openIssueRef = null;
  const issuesMount = mount.querySelector('#pd-issues');
  if (issuesMount && project.number) {
    const ISS_TONE = { Open: 'error', 'In Progress': 'info', Resolved: 'ok' };
    const ISS_NEXT = { Open: 'In Progress', 'In Progress': 'Resolved' };
    const drawIssues = async () => {
      const rows = (await projectIssuesForProject(project.number).catch(() => []))
        .sort((a, b) => String(b.raisedAt || '').localeCompare(String(a.raisedAt || '')));
      // Only when there are issues: logging one is in the ⋯ menu (Add to › Issue).
      issuesMount.innerHTML = rows.length ? `<section class="pd4-sec">
        <div class="pd4-sec-h"><h2>Issues <span class="pd4-sec-meta">${rows.length}</span></h2>${isReviewer ? '<button type="button" class="pd4-link" id="iss-add">+ Log issue</button>' : ''}</div>
        <div class="pd-issue-list">${rows.map((it) => `
          <button class="pd-issue-row" data-id="${escapeHtml(it.id)}">
            <div class="pd-fb-top">${pill(it.status || 'Open', ISS_TONE[it.status] || 'error')}
              <span class="pd-issue-title">${escapeHtml(it.title || '(untitled)')}</span>
              <span class="pd-fb-date">${it.raisedAt ? fmtDate(it.raisedAt) : ''}</span></div>
            ${it.detail ? `<div class="pd-fb-text">${escapeHtml((it.detail || '').slice(0, 140))}</div>` : ''}
          </button>`).join('')}</div></section>` : '';
      const addBtn = issuesMount.querySelector('#iss-add');
      if (addBtn) addBtn.addEventListener('click', openIssueForm);
      issuesMount.querySelectorAll('.pd-issue-row').forEach((row) => row.addEventListener('click', () => {
        const it = rows.find((x) => x.id === row.dataset.id); if (it) openIssueDetail(it);
      }));
    };
    // Log a project issue — creates a new issue record → 520 modal (7a).
    function openIssueForm() {
      openDialog({
        title: 'Log a project issue',
        subtitle: `${project.number} · ${clientName}`,
        width: 520,
        fields: [
          { key: 'title', label: 'Issue (short title)', type: 'text', span: true, required: true, requiredMsg: 'Give it a short title.', placeholder: 'e.g. Beam schedule missing on drawing 05' },
          { key: 'detail', label: 'Detail', type: 'textarea', span: true, optional: true, placeholder: 'What’s the issue?' },
        ],
        primary: {
          label: 'Log issue',
          onSubmit: async (v) => {
            await addProjectIssue({ projectNo: String(project.number), projectId, title: v.title,
              detail: v.detail || null, action: null, status: 'Open',
              raisedBy: session.email, raisedByName: session.displayName, raisedAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
            drawIssues();
          },
        },
      });
    }
    function openIssueDetail(it) {
      const { content, close } = openOverlay({ title: it.title || 'Issue',
        subtitle: `${project.number} · raised ${it.raisedAt ? fmtDate(it.raisedAt) : ''}${it.raisedByName ? ' by ' + it.raisedByName : ''}`, reading: true });
      content.innerHTML = `
        <div style="padding:24px 28px 4px">
          <div class="email-view-meta">${pill(it.status || 'Open', ISS_TONE[it.status] || 'error')}</div>
          <div class="email-reader-body" style="max-width:none">${escapeHtml(it.detail || 'No detail given.')}</div>
          <label class="f-label" style="display:block;margin-top:18px">Action — what's going to be done</label>
          <textarea class="f-textarea" id="iss-action" rows="4" ${isReviewer ? '' : 'disabled'} placeholder="The plan, and progress as it's done…">${escapeHtml(it.action || '')}</textarea>
          <div class="form-error" id="iss-d-err"></div>
        </div>
        <div class="form-foot">
          <button class="btn ghost" id="iss-d-close">Close</button>
          ${(isReviewer && ISS_NEXT[it.status || 'Open']) ? `<button class="btn ghost" id="iss-d-adv">Mark ${ISS_NEXT[it.status || 'Open']}</button>` : ''}
          ${isReviewer ? '<button class="btn primary" id="iss-d-save">Save</button>' : ''}
        </div>`;
      content.querySelector('#iss-d-close').addEventListener('click', close);
      const persist = async (extra) => {
        await updateProjectIssue(it.id, { action: content.querySelector('#iss-action').value.trim() || null, updatedBy: session.email, updatedAt: new Date().toISOString(), ...extra });
        close(); drawIssues();
      };
      const sv = content.querySelector('#iss-d-save');
      if (sv) sv.addEventListener('click', () => persist({}).catch((e) => { content.querySelector('#iss-d-err').textContent = e.message || String(e); }));
      const adv = content.querySelector('#iss-d-adv');
      if (adv) adv.addEventListener('click', () => persist({ status: ISS_NEXT[it.status || 'Open'], resolvedAt: ISS_NEXT[it.status || 'Open'] === 'Resolved' ? new Date().toISOString() : null }).catch((e) => { content.querySelector('#iss-d-err').textContent = e.message || String(e); }));
    }
    openIssueRef = openIssueForm;
    drawIssues();
  }

  // The job's email index, once read, also feeds the tab count, Activity and
  // the Track summary (07/10) — one read, three uses.
  let emailsDone;
  const emailsP = new Promise((r) => { emailsDone = r; });

  // Shared email list (Karan 14/07): read the archiver-built index so EVERY user
  // sees the same TRACK// correspondence, not just whoever's mailbox holds it.
  // Correspondence (17/10/2026): the archiver's shared TRACK// index (Karan
  // 14/07: EVERY user sees the same correspondence) and the WhatsApp filed to
  // the project, in one timeline. Email rows open the SHARED reader exactly as
  // before (handoff C1; no "Open in Gmail"); WhatsApp rows open the chat.
  const shared = mount.querySelector('#pd-emails-shared');
  const openEmail = (en) => en && openMailReader({
    session, entry: en, job: String(project.number), jobKind: 'Project', project,
    // "Track read a deadline" (email canvas turn 2) — offered here, written
    // only by the button, onto the project it is filed against.
    responseBy: project.responseBy || null,
    onSetResponseBy: (d) => updateProjectFields(project.id, { responseBy: d.toISOString() }),
    // With a Gmail token, try the full live thread (real thread strip +
    // attachment chips); the stored plain-text entry is the fallback.
    load: en.threadId ? () => liveThread(en.threadId) : null,
  });
  const corr = mountCorrespondence(shared, {
    project, session, clientName: clientName === '—' ? '' : clientName, clientId: String(project.clientRef || '').split('/').pop(),
    emails: project.number ? null : [],
    onOpenEmail: openEmail,
    whatsapp: canWhatsApp(session && session.email),
    onCount: (n) => { const tabN = mount.querySelector('#pd-email-count'); if (tabN && n != null) tabN.textContent = n ? String(n) : ''; },
    onImport: () => import('./wa-import.js').then((m) => m.openImportDialog({
      project, session, knownIds: corr.knownIds(), knownContacts: corr.contacts(),
      onDone: () => {},
    })),
  });
  if (project.number) {
    projectEmails(project.number, 25, enqData && enqData.number).then((rows) => {
      emailsDone(rows);
      // Compact rows (Karan 08/10, mail-reader's row): bucketed This week /
      // Last week / Earlier, automatic replies folded into one "Auto · n" row.
      corr.setEmails(rows.map(normArchiver));
    }).catch((err) => {
      emailsDone([]);
      corr.setEmails([]);
      shared.insertAdjacentHTML('afterbegin', `<p class="g-note">Couldn’t load emails — ${escapeHtml(err.message || String(err))}</p>`);
    });
  } else {
    emailsDone([]);
  }

  // ══ v4.0.0 — THE PARTS THAT ARRIVE AFTER THE RECORD ══════════════════════

  // ── Instructions: a real table (# · scope · status · fee · hours / budget ·
  // invoiced). Money columns only for the money roles. #2+ can be edited here;
  // instruction 1 too since v4.20.0 (on the server).
  let calcsCount = null;   // kept for the folder read (4. Calculations)
  const hmOf = (mins) => `${Math.floor(mins / 60)}:${String(Math.round(mins % 60)).padStart(2, '0')}`;
  function paintInstructions() {
    const host = mount.querySelector('#instr-list');
    if (!host) return;
    const cols = `${isMoney ? 'pd4-it money' : 'pd4-it'}${isReviewer ? ' has-more' : ''}`;
    const head = `<div class="${cols} pd4-it-h" role="row"><span class="pd4-c-n">#</span><span class="pd4-c-scope">Scope</span><span class="pd4-c-st">Status</span>${isMoney ? '<span class="pd4-c-fee r">Fee</span>' : ''}<span class="pd4-c-hrs r">Hours / budget</span>${isMoney ? '<span class="pd4-c-inv r">Invoiced</span>' : ''}${isReviewer ? '<span class="pd4-c-more" aria-hidden="true"></span>' : ''}</div>`;
    if (!instructions.length) {
      host.innerHTML = `${head}<div class="pd4-it-empty"><div class="pd4-it-empty-t">No instructions yet</div><div class="pd4-it-empty-s">Each instruction carries its own scope, fee, hours and invoices. The first is usually the original scope.</div></div>`;
      return;
    }
    const rows = instructions.slice().sort((a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0)).map((i) => {
      const seq = Number(i.seq) || 1;
      const mins = timePending ? null : (minsByInstr[seq] || 0);
      const who = instrLeadName(i);
      // Instruction 1 too (v4.20.0): the V1 sync that owned it is retired; the
      // edit goes through the server like every other instruction's.
      const editable = isReviewer;
      const revised = Number(i.feeRevisions) > 0;
      return `<div class="${cols} pd4-it-row${editable ? ' is-edit instr-edit' : ''}" data-seq="${escapeHtml(String(i.seq))}" role="row"${editable ? ' tabindex="0" title="Edit this instruction"' : ''}>
        <span class="pd4-it-n pd4-c-n">I${escapeHtml(String(i.seq))}</span>
        <span class="pd4-it-scope pd4-c-scope"><span>${escapeHtml(i.description || 'Original scope')}</span>${who || i.due ? `<span class="pd4-it-sub">${escapeHtml([who, i.due ? `due ${fmtDate(i.due)}` : ''].filter(Boolean).join(' · '))}</span>` : ''}</span>
        <span class="pd4-c-st">${pill(i.status || 'Not Started')}</span>
        ${isMoney ? `<span class="pd4-c-fee r">${escapeHtml(Number(i.fee) ? money(Number(i.fee)) : '—')}${revised ? '<span class="pd4-it-rev" title="Revised — see Fee history">revised</span>' : ''}</span>` : ''}
        <span class="pd4-c-hrs r">${escapeHtml(mins == null ? '…' : hmOf(mins))}<span class="pd4-it-of"> / ${escapeHtml(i.budgetHours ? `${i.budgetHours}h` : '—')}</span></span>
        ${isMoney ? `<span class="pd4-c-inv r">${escapeHtml(invByInstr[seq] ? money(invByInstr[seq]) : '—')}</span>` : ''}
        ${isReviewer ? `<span class="pd4-c-more r"><button type="button" class="pd4-it-more" data-more="${escapeHtml(String(i.seq))}" aria-label="More for instruction ${escapeHtml(String(i.seq))}" title="More">⋯</button></span>` : ''}
      </div>`;
    }).join('');
    host.innerHTML = head + rows;
  }
  paintInstructions();
  // The row menu (v4.20.0): Edit instruction (first — it opens the record),
  // Set the fee or Revise the fee, Fee history. Caught on the table before the
  // row's own click opens the editor.
  mount.querySelector('#instr-list')?.addEventListener('click', (ev) => {
    const b = ev.target && ev.target.closest ? ev.target.closest('[data-more]') : null;
    if (!b) return;
    ev.stopPropagation();
    const instr = instructions.find((i) => String(i.seq) === b.dataset.more);
    if (!instr) return;
    const priced = Number(instr.fee) > 0;
    openRowMenu(b, [
      { label: 'Edit instruction', onClick: () => b.closest('.pd4-it-row')?.click() },
      priced ? { label: 'Revise the fee', onClick: () => feeAction('revise', instr) } : { label: 'Set the fee', onClick: () => feeAction('set', instr) },
      { label: 'Fee history', onClick: () => openFeeHistory({ project, instr }) },
    ]);
  });
  mount.querySelector('#instr-list')?.addEventListener('keydown', (ev) => {
    const r = ev.target.closest('.pd4-it-row.is-edit');
    if (r && (ev.key === 'Enter' || ev.key === ' ')) { ev.preventDefault(); r.click(); }
  });

  // ── Activity: from the project's own records (the golden thread, the
  // registers, the shared email index). A grey dot for messages and system
  // lines, green for a payment, orange for what needs attention.
  let lastEmailRows = [];
  const paintActivity = (emailRows = lastEmailRows) => {
    lastEmailRows = emailRows;
    const host = mount.querySelector('#activity-feed');
    if (!host) return;
    const items = PX.activityItems({
      project: { ...project, clientName: clientName !== '—' ? clientName : '' }, enquiryNo: enqNo || '',
      nodes: gt.nodes || [], documents,
      emails: emailRows.slice(0, 6).map((e) => ({ direction: e.direction, from: e.from, fromName: e.fromName, subject: e.subject, date: asDate(e.date) })),
      dueVal, overdue: lateLive, issuedCount: issued.length, leadName, siteCheck: siteCheckLatest,
    }).slice(0, 12);
    host.innerHTML = items.length ? items.map((x) => `<div class="pd4-act">
        <span class="pd4-dot ${x.tone === 'attn' ? 'attn' : x.pay ? 'ok' : ''}" aria-hidden="true"></span>
        <span class="pd4-act-t">${escapeHtml(x.text)}</span>
        <span class="pd4-act-d">${escapeHtml(x.when ? x.when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '')}</span>
      </div>`).join('') : '<p class="pd4-none">Nothing on record yet.</p>';
  };
  paintActivity();

  // "+ Add a note": a note (or a call) on the project record, shared with the
  // notes written from the Ask Track bar. Not an instruction — it cannot be
  // billed, approved or issued.
  const noteForm = mount.querySelector('#pd-note-form');
  const openNote = () => { if (!noteForm) return; noteForm.hidden = false; mount.querySelector('.pd-tab[data-tab="overview"]')?.click(); mount.querySelector('#pd-note-text')?.focus(); };
  mount.querySelector('#pd-note-add')?.addEventListener('click', openNote);
  mount.querySelector('#pd-note-cancel')?.addEventListener('click', () => { noteForm.hidden = true; });
  mount.querySelector('#pd-note-save')?.addEventListener('click', async (ev) => {
    const ta = mount.querySelector('#pd-note-text');
    const text = String((ta && ta.value) || '').trim();
    if (!text || !project.number) return;
    const b = ev.currentTarget; b.disabled = true; b.textContent = 'Saving…';
    try {
      await addNote({
        text, userName: myName, userEmail: session.email,
        projectNo: String(project.number), projectId: project.id || projectId,
        addedFrom: 'the project record', createdAt: new Date().toISOString(),
      });
      reRender();
    } catch (e) { b.disabled = false; b.textContent = 'Save note'; alert('Couldn’t save the note: ' + (e.message || e)); }
  });

  // A New task dialog, prefilled with this project (resolves with the task, or null).
  const newProjectTaskFor = (title, extra = null, prefill = {}) => new Promise((resolve) => {
    const people = teamFrom(Object.entries(users || {}).map(([id, u]) => ({ id, ...u })), [], dayKey());
    let made = null;
    const d = openNewTask({
      projects: [{ id: projectId, number: project.number || '', name: clientName, site: addr, status: project.status || '' }],
      project: { id: projectId }, people, tasks: [], today: dayKey(), me: meIn(people, session.email), title,
      checkerId: (prefill && prefill.checkerId) || '',
      onCreate: async (fields) => {
        const t = await createTask({ ...fields, ...(extra && extra.fields ? extra.fields : {}),
          description: [fields.description, extra && extra.description].filter(Boolean).join('\n\n') }, session);
        const who = people.find((p) => p.userId === fields.assigneeId);
        made = { id: t.id, assigneeName: who ? who.name : '', checkerId: fields.checkerId || null };
      },
    });
    const scrim = d && d.scrim;
    if (!scrim) { resolve(null); return; }
    const mo = new MutationObserver(() => {
      if (!scrim.isConnected) { mo.disconnect(); resolve(made); if (made && reloadTasksRef) reloadTasksRef(); }
    });
    mo.observe(document.body, { childList: true });
  });
  const tab = (name) => mount.querySelector(`.pd-tab[data-tab="${name}"]`)?.click();

  // ── The alert's fix ──────────────────────────────────────────────────────
  mount.querySelector('[data-alert]')?.addEventListener('click', (ev) => {
    const k = ev.currentTarget.dataset.alert;
    if (k === 'add-original') openAddInstruction({ description: 'Original scope', fee: Number(project.fee) > 0 ? Number(project.fee) : '', budgetHours: Number(project.budgetHours) || '', status: project.status && project.status !== 'On Hold' ? project.status : 'Not Started' });
    else if (k === 'agree-date' || k === 'set-deadline') openDueEdit(ev.currentTarget);
    else if (k === 'record-fee') recordFee();
  });

  // ── Golden thread: a link opens the thread (the enquiry opens its record).
  mount.querySelector('.pd4-gt')?.addEventListener('click', (ev) => {
    const seg = ev.target.closest('[data-gt]'); if (!seg) return;
    if (seg.dataset.gt === 'ENQUIRY' && onOpenEnquiry && instr1 && instr1.enquiryRef) { onOpenEnquiry(relPath(instr1.enquiryRef).split('/').pop()); return; }
    openGT();
  });

  // ── Next up: the open tasks that want something, most urgent first (top 3).
  const firstOf = (id) => { const u = users[id]; return titleCase(String((u && u.displayName) || id || '').split(' ')[0]); };
  const meUid = String(session.email || '').split('@')[0].toLowerCase();
  const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0);
  const ymd = (v) => { const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null; };
  const daysAgo = (v) => { const d = ymd(v) || asDate(v); return d ? Math.max(0, Math.floor((dayStart - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000)) : 0; };
  const shortDay = (v) => { const d = ymd(v); return d ? d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : ''; };
  function nextUpRows() {
    const open = (projTasks || []).filter((t) => t && t.state !== 'done');
    const rows = [];
    for (const t of open.filter((x) => x.state === 'in_review' && x.checkerId)) {
      const n = daysAgo(t.reviewSince || t.lastTouchedAt);
      rows.push({ rank: 0, t, sub: `${firstOf(t.assigneeId) || 'Someone'} → ${firstOf(t.checkerId)}`, when: n ? `waiting ${n} day${n === 1 ? '' : 's'}` : 'sent today', late: n >= 2,
        verb: String(t.checkerId).toLowerCase() === meUid ? 'Check' : 'Open' });
    }
    for (const t of open.filter((x) => x.state === 'blocked')) {
      const b = t.blocker || {};
      rows.push({ rank: 1, t, sub: `Waiting on ${String(b.waitingOnName || 'someone').split(/\s[—–]\s/)[0]}`, when: b.since ? `since ${shortDay(b.since)}` : '', late: false, verb: 'Open' });
    }
    for (const t of open.filter((x) => x.state !== 'in_review' && x.state !== 'blocked')) {
      const d = ymd(t.dueDate);
      if (!d && /^site visit\b/i.test(String(t.title || ''))) { rows.push({ rank: 2, t, sub: 'No date yet', when: '', verb: 'Set date' }); continue; }
      if (!d) continue;
      const n = Math.round((d - dayStart) / 86400000);
      rows.push({ rank: 3 + n / 1000, t, sub: firstOf(t.assigneeId) || 'Nobody yet', when: n < 0 ? `${-n} day${n === -1 ? '' : 's'} overdue` : n === 0 ? 'due today' : n === 1 ? 'due tomorrow' : `due ${shortDay(t.dueDate)}`, late: n < 0, verb: 'Open' });
    }
    return rows.sort((a, b) => a.rank - b.rank).slice(0, 3);
  }
  function paintNextUp() {
    const host = mount.querySelector('#pd-next');
    if (!host) return;
    if (!projTasks) { host.innerHTML = '<p class="pd4-none">Reading the tasks…</p>'; return; }
    const rows = nextUpRows();
    host.innerHTML = rows.length ? rows.map((r) => `<div class="pd4-next">
        <div class="pd4-next-t"><div class="pd4-next-title">${escapeHtml(r.t.title || 'A task')}</div>
          <div class="pd4-next-sub">${escapeHtml(r.sub)}${r.when ? ` · <span class="${r.late ? 'late-text strong' : ''}">${escapeHtml(r.when)}</span>` : ''}</div></div>
        <button type="button" class="btn sm pd4-next-btn" data-task="${escapeHtml(r.t.id)}">${escapeHtml(r.verb)}</button>
      </div>`).join('')
      : '<p class="pd4-none">Nothing waiting. <button type="button" class="pd4-link" data-next-add>Add a task</button></p>';
  }
  mount.querySelector('#pd-next')?.addEventListener('click', (ev) => {
    const b = ev.target.closest('[data-task]');
    if (b && openTaskRef) { openTaskRef(b.dataset.task); return; }
    if (ev.target.closest('[data-next-add]')) newProjectTaskFor('');
  });
  paintNextUp();

  // ── Details: client, lead, site, checker, files. Repainted as the slow reads
  // land (tasks → checker, site check → site line, Drive → files).
  //
  // CHANGE IT WHERE IT IS SHOWN (v4.19.4, Karan 28/10/2026 on 8451: "need to
  // make it easier to change leads etc on projects too"). For the roles that
  // edit the details (reviewers) the lead, checker, client and site are
  // controls in place — a dotted underline and a pencil on hover — that open a
  // small popover (views/inline-edit.js). Everyone else reads plain text.
  // Every write is one the page already made: updateProjectFields (lead with
  // manualOverride — "V2 override wins"; site in the record's own shape, which
  // the site checks pick up by themselves), the check task's checkerId, and
  // changeProjectClient through the Change client confirmation. Each lands in
  // "Changes to this record".
  const people = pickablePeople(users);
  const personName = (id) => (id ? titleCase((users[id] && users[id].displayName) || id) : null);
  const checkerInfo = () => {
    const onInstr = instructions.map((i) => i.checkedByName || i.checkerName || i.reviewerName).find(Boolean);
    if (onInstr) return { name: titleCase(onInstr), fixed: true };
    const t = (projTasks || []).find((x) => x && x.state !== 'done' && x.checkerId);
    return t ? { name: titleCase((users[t.checkerId] && users[t.checkerId].displayName) || t.checkerId), id: t.checkerId, task: t } : { name: '' };
  };
  const logChange = (ln, at) => addRecordChange({ record: 'projects/' + projectId, ...ln, by: session.email, byName: session.displayName, at, money: false })
    .catch((e) => console.warn(`[${ln.field}] change list not written —`, e && e.message));
  async function saveLead(toId) {
    const line = personChange({ field: 'lead', label: 'Lead', fromId: leadId, toId, users });
    if (!line) return;
    if (leadName && line.from !== leadName) line.from = leadName;   // the name the page showed
    const at = new Date().toISOString();
    // The same write as the Projects list's lead picker, plus manualOverride.
    await updateProjectFields(projectId, { lead: toId ? leadRefPath(toId) : null, manualOverride: true, updatedBy: session.email, updatedAt: at });
    await logChange(line, at);
  }
  function editLead(anchor, focusSel) {
    const first = String(leadName || '').split(' ')[0];
    openPersonPicker(anchor, {
      people, currentId: leadId || '', heading: 'Lead', removeLabel: 'Remove lead',
      extra: (leadId && onOpenLead) ? [{ label: `See ${first || 'their'}’s projects`, onClick: () => onOpenLead(leadId, leadName) }] : [],
      onPick: async (id) => { await saveLead(id); await reRender(); refocus(focusSel); },
    });
  }
  async function saveChecker(info, toId) {
    const line = personChange({ field: 'checker', label: 'Checker', fromId: info.id || null, toId, users });
    if (!line) return;
    if (info.task) {
      // The check is a task with a checker, so changing the checker changes it there.
      const at = new Date().toISOString();
      await updateTask(info.task.id, { checkerId: toId || null }, session);
      info.task.checkerId = toId || null;
      await logChange(line, at);
      paintDetails();
      refocus('[data-det="checker"]');
      if (reloadTasksRef) reloadTasksRef();
      return;
    }
    // No check yet: raise one with the checker already picked (New task, as
    // Assign always did), once this popover has closed.
    setTimeout(async () => {
      const made = await newProjectTaskFor(`Check the calculations for ${project.number || 'this project'}`, null, { checkerId: toId });
      if (made && made.checkerId) await logChange({ field: 'checker', label: 'Checker', from: null, to: personName(made.checkerId) }, new Date().toISOString());
    }, 0);
  }
  function editChecker(anchor) {
    const info = checkerInfo();
    openPersonPicker(anchor, {
      people, currentId: info.id || '', heading: 'Checker', removeLabel: info.task ? 'Remove checker' : '',
      onPick: (id) => saveChecker(info, id),
    });
  }
  async function saveSite(text, meta) {
    const fields = siteFields(text, meta);
    const at = new Date().toISOString();
    await updateProjectFields(projectId, { ...fields, manualOverride: true, updatedBy: session.email, updatedAt: at });
    if ((fields.site || '') !== (addr || '')) await logChange({ field: 'site', label: 'Site address', from: addr || null, to: fields.site }, at);
  }
  function editSite(anchor) {
    const sa = project.siteAddress && typeof project.siteAddress === 'object' ? project.siteAddress : {};
    openAddressEditor(anchor, {
      value: addr,
      meta: { lat: project.siteLat ?? sa.lat ?? null, lng: project.siteLng ?? sa.lng ?? null, placeId: project.sitePlaceId ?? sa.placeId ?? null, postcode: sa.postcode || null },
      onSave: async (text, meta) => { await saveSite(text, meta); await reRender(); refocus('[data-det="addr"]'); },
    });
  }
  function changeClientHere() {
    chooseClient((pick) => {
      if (!pick) return;
      confirmClientChange(pick).then((ok) => { if (ok) reRender().then(() => refocus('[data-det="client-edit"]')); })
        .catch((e) => toastV4(`The client did not change — ${(e && e.message) || e}`, 6000));
    });
  }
  function editClient(anchor) {
    if (!(onOpenClient && client)) { changeClientHere(); return; }
    openProjectMenu({ anchor, align: 'left' }, [
      { label: `Open ${shownClient || 'the client'}`, onClick: () => onOpenClient(client.id) },
      { label: 'Change client…', onClick: () => changeClientHere() },
    ], { width: 240 });
  }
  const editBtnHtml = (det, text, { empty = false, label = '' } = {}) =>
    `<button type="button" class="pd4-edit${empty ? ' is-empty' : ''}" data-det="${det}" aria-haspopup="dialog"${label ? ` aria-label="${escapeHtml(label)}"` : ''}>${escapeHtml(text)}</button>`;

  const folderHref = project.folderUrl || (project.folderId ? `https://drive.google.com/drive/folders/${project.folderId}` : '');
  function paintDetails() {
    const host = mount.querySelector('#pd-details');
    if (!host) return;
    const snap = siteCtl ? siteCtl.latest : siteCheckLatest;
    const tier = snap ? tierOf(snap) : (project.siteLat != null && project.siteLng != null ? 'address' : 'postcode');
    const tierWord = tier === 'pin' ? 'Pinned' : tier === 'address' ? 'Address matched' : 'Postcode only';
    const flags = snap && Array.isArray(snap.flags) ? snap.flags.filter((f) => f.severity === 'warning' || f.severity === 'action').length : 0;
    const checkedLine = snap ? (flags ? `${flags} constraint${flags === 1 ? '' : 's'} to note` : 'constraints checked') : 'constraints not checked';
    const nFiles = job && !job.error ? (job.folders || []).reduce((n, f) => n + ((f.files && f.files.length) || 0), 0) : null;
    const filesText = !project.number ? 'No project number' : job && job.error === 'nofolder' ? 'No Drive folder found'
      : job && job.error ? 'Drive could not be read' : nFiles == null ? (hasToken() ? 'Reading the folder…' : 'Connect Google to read it')
      : nFiles ? `${nFiles} file${nFiles === 1 ? '' : 's'} in ${(job.folders || []).filter((f) => f.files && f.files.length).length} folder${(job.folders || []).filter((f) => f.files && f.files.length).length === 1 ? '' : 's'}` : 'Drive folder is empty';
    const chk = checkerInfo();
    const canRerun = !!(session.roles || []).some((r) => ['Owner', 'Director', 'Coordinator', 'Engineer'].includes(r));
    const clientHtml = canChangeClient
      ? editBtnHtml('client-edit', clientName, { label: `Client: ${clientName}. Open or change` })
      : (onOpenClient && client ? `<button type="button" class="pd4-link" data-det="client">${escapeHtml(clientName)}</button>` : escapeHtml(clientName));
    const leadHtml = isReviewer
      ? editBtnHtml('lead', leadName || 'No lead', { empty: !leadName, label: leadName ? `Lead: ${leadName}. Change the lead` : 'No lead. Choose a lead' })
      : escapeHtml(leadName || 'No lead');
    const checkerHtml = (isReviewer && !chk.fixed)
      ? editBtnHtml('checker', chk.name || 'Not assigned', { empty: !chk.name, label: chk.name ? `Checker: ${chk.name}. Change the checker` : 'No checker. Choose one' })
      : escapeHtml(chk.name || 'Not assigned');
    // A repaint (tasks or the site check arriving) must not steal the keyboard
    // from the row someone is on.
    const had = host.contains(document.activeElement) && document.activeElement.dataset ? document.activeElement.dataset.det : null;
    host.innerHTML = `
      <div><dt>Client</dt><dd>${clientHtml}
        ${contact || liveForClient ? `<div class="pd4-dd-sub">${escapeHtml([contact ? `Contact: ${contact}` : '', liveForClient ? `${liveForClient} live project${liveForClient === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · '))}</div>` : ''}</dd></div>
      <div><dt>Lead</dt><dd>${leadHtml}</dd></div>
      <div><dt>Site</dt><dd>${addr ? `${isReviewer ? editBtnHtml('addr', addr, { label: `Site: ${addr}. Change the address` }) : escapeHtml(addr)}<div class="pd4-dd-sub">${escapeHtml(`${tierWord} · ${checkedLine}`)}</div>
        <div class="pd4-dd-links"><button type="button" class="pd4-link" data-det="pin">${tier === 'pin' ? 'Move the pin' : 'Drop a pin'}</button>${snap ? '<button type="button" class="pd4-link" data-det="checks">See site checks</button>' : canRerun ? '<button type="button" class="pd4-link" data-det="run">Run site checks</button>' : ''}</div>`
        : `No site address${isReviewer ? ' · <button type="button" class="pd4-link" data-det="addr" aria-haspopup="dialog">Add</button>' : ''}`}</dd></div>
      <div><dt>Checker</dt><dd>${checkerHtml}</dd></div>
      <div><dt>Files</dt><dd>${escapeHtml(filesText)}${folderHref ? ' · <button type="button" class="pd4-link" data-det="files">Open</button>' : ''}</dd></div>`;
    if (had) host.querySelector(`[data-det="${had}"]`)?.focus();
  }
  mount.querySelector('#pd-details')?.addEventListener('click', (ev) => {
    const btn = ev.target.closest('[data-det]');
    const k = btn && btn.dataset.det;
    if (!k) return;
    if (k === 'client' && onOpenClient && client) onOpenClient(client.id);
    else if (k === 'client-edit' && canChangeClient) editClient(btn);
    else if (k === 'lead' && isReviewer) editLead(btn, '[data-det="lead"]');
    else if (k === 'checker' && isReviewer) editChecker(btn);
    else if (k === 'pin') {
      if (siteCtl) siteCtl.openPin();
      else import('./site-pin.js').then(({ openSitePin }) => openSitePin({ projectId, project, onConfirmed: () => reRender() })).catch((e) => console.error('[site-pin] could not open', e));
    } else if (k === 'run' && siteCtl) { const h = mount.querySelector('#pdx-sc'); if (h) h.hidden = false; siteCtl.rerun('manual'); }
    else if (k === 'checks' && siteCtl) siteCtl.openDrawer();
    else if (k === 'addr' && isReviewer) editSite(btn);
    else if (k === 'files') window.open(safeUrl(folderHref), '_blank', 'noopener');
  });
  mount.querySelector('#lead-edit')?.addEventListener('click', (ev) => editLead(ev.currentTarget, '#lead-edit'));

  // ── References (Karan 02/11/2026: "Needs to be a place to insert planning
  // references, building control references, and also POs into a project").
  // Planning, building control and the client's POs, more than one of each
  // (project-refs.js). Everyone reads them; the reviewers change them in place,
  // like the Details above: one updateProjectFields per save (manualOverride, as
  // every V2 edit) and one "Changes to this record" line per reference added or
  // removed. A client PO can be tied to an instruction; the invoice draft starts
  // with it (views/invoice-draft.js, poForInvoice).
  const REF_ORDER = ['planning', 'buildingControl', 'clientPo'];
  const instrLabel = (seq) => {
    const i = instructions.find((x) => Number(x.seq) === Number(seq));
    return `Instruction ${seq}${i && i.description ? ` · ${String(i.description).slice(0, 40)}` : ''}`;
  };
  function paintRefs() {
    const host = mount.querySelector('#pd-refs');
    if (!host) return;
    host.innerHTML = REF_ORDER.map((kind) => {
      const k = REF_KINDS[kind];
      const list = refsOf(project, kind);
      const sub = (x) => [x.note || '', k.perInstruction && x.instructionSeq ? instrLabel(x.instructionSeq) : ''].filter(Boolean).join(' · ');
      const lines = list.map((x) => `<div class="pd4-refline">${escapeHtml(x.ref)}${sub(x) ? `<div class="pd4-dd-sub">${escapeHtml(sub(x))}</div>` : ''}</div>`).join('');
      let dd;
      if (!isReviewer) dd = list.length ? lines : '<span class="pd4-dd-sub">None</span>';
      else if (!list.length) dd = `<button type="button" class="pd4-link" data-ref="${kind}" aria-haspopup="dialog" aria-label="Add a ${escapeHtml(k.label.toLowerCase())}">Add</button>`;
      else dd = `${editBtnHtml('ref-' + kind, list.map((x) => x.ref).join(', '), { label: `${k.label}s: ${list.map((x) => x.ref).join(', ')}. Add or change` })}${list.some((x) => sub(x)) ? `<div class="pd4-dd-sub">${escapeHtml(list.map(sub).filter(Boolean).join(' · '))}</div>` : ''}`;
      return `<div><dt>${escapeHtml(k.row)}</dt><dd>${dd}</dd></div>`;
    }).join('');
  }
  async function saveRefs(kind, next) {
    const before = refsOf(project, kind);
    const lines = refChanges(kind, before, next);
    if (!lines.length) return;
    const at = new Date().toISOString();
    const fields = refsFields(kind, next);
    await updateProjectFields(projectId, { ...fields, manualOverride: true, updatedBy: session.email, updatedAt: at });
    Object.assign(project, fields);
    for (const ln of lines) await logChange(ln, at);
  }
  function editRefs(kind, anchor) {
    const k = REF_KINDS[kind];
    openRefsEditor(anchor, {
      heading: k.row === 'Client PO' ? 'Client POs' : `${k.row} references`,
      items: refsOf(project, kind),
      placeholder: k.placeholder,
      withInstruction: !!k.perInstruction,
      instructions: instructions.slice().sort((a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0)).map((i) => ({ seq: i.seq, label: instrLabel(i.seq) })),
      addRef: (list, entry) => addRef(list, entry, { by: session.email, at: new Date().toISOString() }, kind),
      onSave: async (next) => { await saveRefs(kind, next); paintRefs(); renderProjectChangeList(); refocus(`#pd-refs [data-det="ref-${kind}"], #pd-refs [data-ref="${kind}"]`); },
    });
  }
  mount.querySelector('#pd-refs')?.addEventListener('click', (ev) => {
    if (!isReviewer) return;
    const btn = ev.target.closest('[data-det^="ref-"], [data-ref]');
    if (!btn) return;
    const kind = btn.dataset.ref || btn.dataset.det.slice(4);
    if (REF_KINDS[kind]) editRefs(kind, btn);
  });
  paintRefs();

  // ── ⋯ (§13, p48): "Add to 8601", then the folder, the link and the endings.
  const moreBtn = mount.querySelector('#pd-more');
  const canCalc = canCalculate(session && session.email);
  moreBtn?.addEventListener('click', (ev) => {
    ev.stopPropagation();
    const items = [
      isReviewer ? { label: 'Instruction', icon: 'files', onClick: () => startAddInstruction() } : null,
      { label: 'Task', icon: 'tasks', onClick: () => newProjectTaskFor('') },
      isReviewer && project.number ? { label: 'Issue', icon: 'warning', onClick: () => openIssueRef && openIssueRef() } : null,
      project.number ? { label: 'Note or call', icon: 'enquiries', onClick: openNote } : null,
      // Track has no upload of its own yet: the folder opens in Drive to drop files in.
      folderHref ? { label: 'Upload files', icon: 'upload', hint: 'in Drive', onClick: () => window.open(safeUrl(folderHref), '_blank', 'noopener') } : null,
      canCalc ? { label: 'Calculation package', icon: 'calculate', onClick: () => goToCalculate(`project-${projectId}`) } : null,
      (editPatterns || isReviewer) ? { label: 'Edit details', sep: true, onClick: () => (mount.querySelector('#edit-project-panel') || mount.querySelector('#edit-project'))?.click() } : null,
      folderHref ? { label: 'Open Drive folder', sep: !(editPatterns || isReviewer), onClick: () => window.open(safeUrl(folderHref), '_blank', 'noopener') } : null,
      { label: 'Copy link', sep: !(editPatterns || isReviewer) && !folderHref, onClick: () => {
        const url = `${location.origin}/projects/${encodeURIComponent(project.number || projectId)}`;
        (navigator.clipboard ? navigator.clipboard.writeText(url) : Promise.reject()).then(() => PX_toast('Link copied'), () => PX_toast(url));
      } },
      { label: 'Put on hold', sep: true, ending: true, role: isReviewer ? '' : 'Reviewer',
        allowed: isReviewer && !['On Hold', 'Void', 'Complete', 'Issued'].includes(project.status), onClick: () => openHoldConfirm() },
      { label: 'Void — never started', ending: true, role: canStatus ? '' : 'Owner · Director',
        allowed: canStatus && !['Void', 'Complete', 'Issued'].includes(project.status), onClick: () => openVoidConfirm() },
    ].filter(Boolean);
    openProjectMenu({ anchor: moreBtn, align: 'right' }, items, { header: `Add to ${project.number || 'this project'}`, width: 282 });
  });
  function PX_toast(msg) {
    const t = document.createElement('div');
    t.className = 'inline-toast'; t.setAttribute('role', 'status'); t.textContent = msg;
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 3200);
  }

  // ── The project folder, read ONCE with Files' own loader (and its per-person
  // cache, shared with the Files screen): Document control, the Files list and
  // the Documents tab's folder table. Folders open inside Track.
  let job = null, ev = null;
  const openFolder = (id) => {
    const fo = job && job.folders.find((f) => f.id === id);
    if (!fo) return;
    const { content, close } = openOverlay({ title: `${project.number} · ${fo.name}`, subtitle: clientName, wide: true });
    content.classList.add('pdx-folder-host');
    renderFolderView(content, {
      session, project: String(project.number), codeAt: 0,
      trail: [{ id: fo.id, name: fo.name, webViewLink: fo.webViewLink }],
      home: { label: `${project.number} folders`, onClick: close },
    });
  };
  let driveErr = null;
  paintDetails();
  let dcAll = false;                     // "Show all n" on the Before issue list
  const paintDrive = () => {
    // A failed Drive read used to leave "Reading Drive…" on screen for good.
    const failed = (job && job.error) || driveErr;
    const dc = failed && !ev
      ? { tone: 'grey', verdict: 'Not checked', headline: job && job.error === 'nofolder' ? `No Drive folder found for ${project.number}.` : `Drive could not be read just now (${String(failed).slice(0, 80)}).` }
      : PX.docControl(ev);
    const dcHost = mount.querySelector('#pdx-dc');
    const open = !mount.querySelector('#pdx-dc-open')?.hidden;
    if (dcHost) dcHost.innerHTML = PX.docControlHtml(dc, { number: project.number || '', open });
    const fixes = mount.querySelector('#pdx-dc-fixes');
    if (fixes) fixes.innerHTML = PX.docFixesHtml(ev, { max: dcAll ? Infinity : 8 });
    const rows = PX.folderRows(job, ev);
    const fl = mount.querySelector('#pdx-files');
    if (fl) fl.innerHTML = job && job.error ? `<p class="g-note">${escapeHtml(job.error === 'nofolder' ? `No Drive folder found for ${project.number}.` : job.error)}</p>` : PX.filesListHtml(rows);
    const ft = mount.querySelector('#pd-files');
    if (ft) ft.innerHTML = job && !job.error ? PX.folderTableHtml(rows) : `<p class="g-note">${escapeHtml(job && job.error === 'nofolder' ? `No Drive folder found for ${project.number}.` : `Drive could not be read just now${failed ? ` (${String(failed).slice(0, 80)})` : ''}.`)}</p>`;
    const nFiles = rows.reduce((n, r) => n + (r.count || 0), 0);
    const docN = mount.querySelector('#pd-doc-count');
    if (docN && nFiles) docN.textContent = ` ${nFiles}`;
    const calcs = job && job.folders.find((f) => f.code === '04');
    calcsCount = calcs ? calcs.files.filter((f) => !/superseded/i.test((f.path || []).join('/'))).length : (job && !job.error ? 0 : null);
    paintInstructions();
    paintDetails();
  };
  page.addEventListener('click', (e) => {
    const row = e.target.closest('[data-folder]');
    if (row && page.contains(row)) { openFolder(row.dataset.folder); return; }
    if (e.target.closest('[data-dc-all]')) { dcAll = true; paintDrive(); return; }
    if (e.target.closest('#pdx-dc-row')) {
      const box = mount.querySelector('#pdx-dc-open');
      if (box) { box.hidden = !box.hidden; paintDrive(); }
    }
  });
  page.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const row = e.target.closest && e.target.closest('[data-folder], #pdx-dc-row');
    if (!row || !page.contains(row)) return;
    e.preventDefault(); row.click();
  });
  mount.querySelector('#files-open-all')?.addEventListener('click', () => tab('documents'));
  const driveP = (async () => {
    if (!project.number) return;
    if (!hasToken()) await resumeToken().catch(() => false);
    if (!hasToken()) {
      const msg = '<p class="g-note">Connect Google on Mail or Files to see this project’s folders here.</p>';
      ['#pdx-files', '#pd-files'].forEach((sel) => { const h = mount.querySelector(sel); if (h) h.innerHTML = msg; });
      const dcHost = mount.querySelector('#pdx-dc');
      if (dcHost) dcHost.innerHTML = PX.docControlHtml({ tone: 'grey', verdict: 'Not connected', headline: 'Connect Google to check the folders against the house numbering.' }, { number: project.number });
      return;
    }
    const key = driveKey(session && session.email, 'project', String(project.number), String(project.folderId || 'by-number'));
    await swrMany({
      keys: [key],
      fetchAll: () => loadProjects([project], session && session.email),
      onData: (vals) => {
        const v = vals[0];
        if (!v) return;
        job = { ...v, project };
        ev = v.error ? null : evaluate(job);
        if (mount.isConnected) paintDrive();
      },
      onError: (e) => { driveErr = (e && e.message) || 'Drive could not be read'; if (mount.isConnected) paintDrive(); },
    }).catch((e) => { driveErr = (e && e.message) || 'Drive could not be read'; if (mount.isConnected) paintDrive(); });
    // LIVE (07/10/2026): a file added to this project's folders shows within
    // ~30 s — document control, the to-fix list and the folder counts follow.
    if (mount.__driveLive) mount.__driveLive();
    mount.__driveLive = startDriveLive({
      mount, who: session && session.email,
      watched: () => (job && job.watchIds) || [],
      known: () => (job && job.known) || {},
      onTouched: async () => {
        const [v] = await loadProjects([project], session && session.email);
        if (!v || v.error || !mount.isConnected) return;
        driveStore.set(key, { value: v, at: Date.now() }).catch(() => {});
        job = { ...v, project };
        ev = evaluate(job);
        driveErr = null;
        paintDrive();
      },
    });
  })();

  // ── Site constraints: lazy, after this paint, never holding the record.
  // The card reads getSiteChecks itself; a missing or slow function costs the
  // card its own "could not read" line and nothing else on the page.
  {
    const scHost = mount.querySelector('#pdx-sc');
    if (scHost) {
      const enquiryId = (instr1 && instr1.enquiryRef) ? relPath(instr1.enquiryRef).split('/').pop() : ((enqData && enqData.id) || null);
      setTimeout(() => {
        if (!mount.isConnected) return;
        try {
          siteCtl = mountSiteConstraints({
            page, host: scHost, mastHost: mount.querySelector('#scb-mast-host'), siteExtraHost: null,
            projectId, project, session,
            enquiryId: onOpenEnquiry ? enquiryId : null, onOpenEnquiry,
            // "Add address": the Site row's own in-place search (v4.19.4); Edit
            // details at the Site field when the row is not there to anchor it.
            onAddAddress: () => {
              const at = isReviewer && mount.querySelector('#pd-details [data-det="addr"]');
              if (at) { at.scrollIntoView({ block: 'center' }); editSite(at); return; }
              (mount.querySelector('#edit-project-panel') || mount.querySelector('#edit-project'))?.click();
              setTimeout(() => {
                const inp = document.querySelector('#pe-site') || document.querySelector('.tf-panel .tf-field[data-key="site"] input');
                if (inp) { inp.scrollIntoView({ block: 'center' }); inp.focus(); }
              }, 280);
            },
            // A flag's "Add to tasks": the ordinary New task dialog, prefilled,
            // as a 'system' task labelled SITE FLAG that points back at the flag.
            onTask: ({ title, description, sourceRef }) => newProjectTaskFor(title, {
              description, fields: { source: 'system', sourceLabel: 'SITE FLAG', sourceRef },
            }),
            onLoaded: (latest) => { siteCheckLatest = latest || null; scHost.hidden = !latest; paintActivity(); paintDetails(); },
          });
        } catch (e) { console.warn('[site checks] block —', e && e.message); }
      }, 0);
    }
  }

  // The shared email index, once read, also feeds Activity. (The Track summary
  // prose block left the record with v4.0.0, design p07 — and with it its call.)
  emailsP.then((emailRows) => paintActivity(emailRows));
}
